import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { Link, useRoute } from "wouter";
import {
  useAccount,
  useChainId,
  useSwitchChain,
  useReadContracts,
  usePublicClient,
  useWalletClient,
  useSignMessage,
  useBalance,
} from "wagmi";
import { mainnet } from "wagmi/chains";
import {
  formatUnits,
  parseUnits,
  zeroAddress,
  WaitForTransactionReceiptTimeoutError,
  type Address,
} from "viem";
import "@/styles/app.css";
import { ThemeToggle } from "@/lib/theme";
import { WalletButton, useWalletUI } from "@/lib/wallet-ui";
import { useCurveStats } from "@/lib/curve-stats";
import { useMarketStats } from "@/lib/market";
import { BurnSupplyMeter } from "@/components/burn-supply";
import { BondStatusCard } from "@/components/bond-status";
import { StatStrip } from "@/components/stat-strip";
import { InfoPanel } from "@/components/privacy-info-panel";
import {
  CURVE_ADDRESS,
  ROUTER_ADDRESS,
  UNISWAP_SWAP_URL,
  USDS_ADDRESS,
  USDC_ADDRESS,
  USDT_ADDRESS,
  SUSDS_ADDRESS,
  SHIELDED_LENDING_ADDRESS,
  ETH_USD_FEED,
  ASSET_DECIMALS,
  type PayAsset,
  type SellAsset,
  curveAbi,
  erc20Abi,
  routerAbi,
  aggregatorAbi,
  shieldedPoolAbi,
  shieldedLendingAbi,
  shieldedUsd0Abi,
  sUsdsAbi,
  explorerAddressUrl,
  explorerTxUrl,
} from "@/lib/contracts";
import { PRIVACY_CONFIG, poolAddress, privatePoolConfigured, usd0PrivacyConfigured } from "@/lib/privacy-config";
import { loadRelayerRegistry, normalizeCustomRelayerUrl, type RelayerListing } from "@/lib/relayer-registry";
import { shieldedPoolV2Abi, verifierWrapperV2Abi, feeControllerV2Abi, usd0V2Abi, usd0GatewayAbi } from "@/lib/v2-contracts";
import {
  type Keypair as PrivateKeypair,
  type RelayerPool as PrivateRelayerPool,
  type ShieldedPoolId as PrivatePoolId,
  unlockOrCreateShieldedWallet,
  fetchRelayerStatus as fetchPrivateRelayerStatus,
  waitForPoolIndex as waitForPrivatePoolIndex,
  syncPool as syncPrivatePool,
  privateBalance as privatePoolBalance,
  buildPrivateShield,
  buildPrivateTransfer,
  buildPrivateUnshield,
  submitPrivateViaRelayer,
  toContractArgs as toPrivateContractArgs,
} from "@/lib/private-pool/lazy";
import {
  type Keypair,
  type RelayerInfo,
  type ShieldedPoolId,
  deriveKeypair,
  fetchPoolState,
  fetchRelayerInfo,
  fetchLoansFor,
  fetchAllLoans,
  scanOwnedNotes,
  balanceOf,
  buildShield,
  buildTransfer,
  buildWithdraw,
  buildCollateralWithdraw,
  buildReshieldDeposit,
  ownerCommitmentFor,
  emptyTransactArgs,
  submitViaRelayer,
  toTransactArgs,
  clearProvingCache,
  prewarmProvingAssets,
} from "@/lib/shielded/lazy";
import {
  setProvingProgress,
  getProvingProgress,
  subscribeProvingProgress,
} from "@/lib/shielded/progress-store";

// ============================================================
// 01 dapp. Faithful port of the mock (the visual source of
// truth). Trade and the live stat strip read and write the real
// Curve01 contract on Ethereum mainnet. Buys with ETH, USDC, or
// USDT route through the live multi-asset router; USDS buys go
// straight to the curve. privacy is configured from deployment
// environment variables: shields are self-submitted, while private sends
// and withdrawals use the relayer. Legacy private lending is not routed.
// ============================================================

type View = "trade" | "private" | "borrow" | "usd0" | "account" | "info";
type PrivTab = "shield" | "send" | "unshield";
type AvailableRelayer = RelayerListing & { relayer: Address; pool: PrivateRelayerPool };

const VIEWS: View[] = ["trade", "private", "usd0", "account", "info"];

const E12 = 10n ** 12n; // scale 6-decimal stablecoins up to the curve's 18

// Apply a slippage tolerance (in basis points) to an on-chain output,
// producing the minimum the caller is willing to accept.
function applySlip(out: bigint, bps: bigint): bigint {
  return out - (out * bps) / 10000n;
}

function num(n: number, d = 0): string {
  return Number(n).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
}
function usd(n: number, d = 4): string {
  return "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
}
function usd2(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function pctStr(n: number | null | undefined, dp = 1): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return n.toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp }) + "%";
}
function shortAddr(a: string): string {
  return `${a.slice(0, 6)}…${a.slice(-4)}`;
}
// Parse user input into a bigint at the given token decimals; 0n on invalid.
function parseDec(s: string, decimals: number): bigint {
  const t = s.trim();
  if (!t || !/^\d*\.?\d*$/.test(t)) return 0n;
  try {
    return parseUnits(t, decimals);
  } catch {
    return 0n;
  }
}
function protocolFee30Bps(amount: bigint): bigint {
  return amount > 0n ? (amount * 30n + 9_999n) / 10_000n : 0n;
}
function maxPrivateSend(available: bigint): bigint {
  let low = 0n;
  let high = available;
  while (low < high) {
    const middle = (low + high + 1n) / 2n;
    if (middle + protocolFee30Bps(middle) <= available) low = middle;
    else high = middle - 1n;
  }
  return low;
}
// Format a bigint at the given token decimals into a display number.
function numU(v: bigint, decimals: number, d = 2): string {
  return num(Number(formatUnits(v, decimals)), d);
}
// Format an 18-decimal bigint for display; "—" when not yet loaded.
function fmtUnits(v: bigint | undefined, d = 2): string {
  if (v === undefined) return "—";
  return num(Number(formatUnits(v, 18)), d);
}
// A small "Balance N SYM" tag shown above a trade field, with an optional Max
// fill. Renders nothing until the balance has loaded so we never imply a value
// we do not yet have.
function BalTag({
  v,
  dec,
  sym,
  onMax,
}: {
  v: bigint | undefined;
  dec: number;
  sym: string;
  onMax?: () => void;
}): ReactNode {
  if (v === undefined) return null;
  return (
    <span className="bal-tag">
      Balance <span className="mono">{numU(v, dec, dec === 18 ? 4 : 2)}</span> {sym}
      {onMax ? (
        <button type="button" className="bal-max" onClick={onMax}>
          Max
        </button>
      ) : null}
    </span>
  );
}
// A small inline "copy" affordance for an address the user shares. Renders
// nothing when the Clipboard API is unavailable, so the button is never a no-op.
function CopyButton({ text }: { text: string }): ReactNode {
  const [done, setDone] = useState(false);
  if (typeof navigator === "undefined" || !navigator.clipboard) return null;
  return (
    <button
      type="button"
      aria-label={done ? "address copied" : "copy address"}
      onClick={() => {
        navigator.clipboard
          .writeText(text)
          .then(() => {
            setDone(true);
            setTimeout(() => setDone(false), 1500);
          })
          .catch(() => {});
      }}
      style={{
        marginLeft: "0.5rem",
        fontFamily: "var(--sans)",
        fontSize: "0.72rem",
        fontWeight: 600,
        color: "var(--accent)",
        background: "none",
        border: 0,
        padding: 0,
        cursor: "pointer",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
      }}
    >
      {done ? "copied" : "copy"}
    </button>
  );
}
function txPending(hash: string): ReactNode {
  return (
    <>
      Waiting for confirmation.{" "}
      <a href={explorerTxUrl(hash)} target="_blank" rel="noreferrer">
        View on Etherscan
      </a>
    </>
  );
}
function txDone(msg: string, hash: string): ReactNode {
  return (
    <>
      {msg}{" "}
      <a href={explorerTxUrl(hash)} target="_blank" rel="noreferrer">
        View on Etherscan
      </a>
    </>
  );
}
// Live proof progress, rendered in its own tiny component that subscribes to an
// external store. The download fires progress many times a second; routing it
// through the giant Dapp component's state re-rendered the whole tree on every
// tick and froze the page mid-download. This isolates those updates so only this
// banner re-renders.
function ProvingProgress(): ReactNode {
  const progress = useSyncExternalStore(
    subscribeProvingProgress,
    getProvingProgress,
    () => null,
  );
  if (!progress) return null;
  if (progress.phase === "prove") {
    return (
      <div className="banner info" style={{ marginTop: "var(--s-3)", marginBottom: 0 }}>
        Building the proof in your browser. This can take up to a minute.
      </div>
    );
  }

  const total = progress.total > 0 ? progress.total : 214_900_748;
  const loaded = Math.min(progress.loaded, total);
  const pct = total > 0 ? Math.min(100, (loaded / total) * 100) : 0;
  const mb = (bytes: number): string => (bytes / 1_000_000).toFixed(1);
  return (
    <div className="banner info proving-progress" style={{ marginTop: "var(--s-3)", marginBottom: 0 }}>
      <div className="proving-progress-head">
        <span>Downloading proving key</span>
        <span className="mono">{mb(loaded)} MB / {mb(total)} MB</span>
      </div>
      <div
        className="proving-progress-track"
        role="progressbar"
        aria-label="Proving key download"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={loaded}
      >
        <span style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// The private proof downloads a large key and runs the whole computation inside
// this page, which needs more memory than a phone browser hands a single tab.
// Without this check the tab can be killed part way through the download and
// drop to a blank screen, so stop early with an honest message and point the
// user at a desktop browser. Returns the message to show, or null when the
// device looks capable.
function provingMemoryBlock(): string | null {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent || "";
  const isPhone =
    /iPhone|iPod|Android.*Mobile|Windows Phone|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  const isTablet = /iPad|Android(?!.*Mobile)|Tablet|PlayBook|Silk/i.test(ua);
  // Modern iPadOS reports a desktop user agent, so also treat pure touch devices
  // (a coarse pointer with no fine pointer available) as not capable. A laptop
  // with a touchscreen still exposes a fine pointer, so it is not blocked.
  const touchOnly =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches &&
    !window.matchMedia("(any-pointer: fine)").matches;
  const mem = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  const lowMem = typeof mem === "number" && mem > 0 && mem <= 2;
  if (isPhone || isTablet || touchOnly || lowMem) {
    return "The private proof runs inside this page and needs more memory than a phone or tablet browser can give. Open 01 in a desktop browser to shield, send, or withdraw privately.";
  }
  return null;
}

// True when 01 is running inside another page's frame (for example an embedded
// preview), rather than as its own top-level tab.
function inEmbeddedFrame(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.self !== window.top;
  } catch {
    // A cross-origin parent makes window.top unreadable, which only happens when
    // we are framed, so treat that as embedded too.
    return true;
  }
}

// Guard the private flow before it begins the heavy work: downloading the large
// one-time proving key and running the whole proof inside this page. Two contexts
// cannot finish it. An embedded preview frame has a per-frame memory cap far
// below what the key download plus the proof need, so the page is killed part
// way through (this is the "crash at a fixed percent" while the key downloads);
// since the user can fix this by opening 01 on its own, the message links them to
// a standalone tab. A phone or tablet browser also cannot spare the memory, so it
// falls back to the existing explanation. Returns the banner to show, or null
// when the context looks capable.
function provingEnvBlock(): { cls: string; text: ReactNode } | null {
  if (inEmbeddedFrame()) {
    const href = typeof window !== "undefined" ? window.location.href : "";
    return {
      cls: "banner warn",
      text: (
        <>
          This preview runs inside a frame that cannot spare enough memory for the
          private proof, which downloads a large one-time key and runs the whole
          computation in the page.{" "}
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            style={{ color: "inherit", textDecoration: "underline" }}
          >
            Open 01 in its own browser tab
          </a>{" "}
          to shield, send, or withdraw privately.
        </>
      ),
    };
  }
  const memBlock = provingMemoryBlock();
  if (memBlock) return { cls: "banner warn", text: memBlock };
  return null;
}

type ErrFlow = "trade" | "private";

function humanError(
  err: unknown,
  opts: { isBuy?: boolean; flow?: ErrFlow } = {},
): string {
  const { isBuy = false, flow = "trade" } = opts;
  // Surface the real cause in the console so failures are diagnosable. The
  // banner stays short and honest; the console keeps the underlying detail.
  console.error("[01] action failed:", err);
  const m = err instanceof Error ? err.message : String(err);
  if (/user rejected|denied|rejected the request|user denied/i.test(m)) {
    return "You rejected the request in your wallet.";
  }
  // Proving and pool-sync failures only happen on the private flow, so keep
  // these specific messages scoped to it to avoid mislabeling trade errors.
  if (flow === "private") {
    // The proving files could not be downloaded from storage.
    if (/zkey|\.wasm|transaction_final|public-objects\/shielded|proving asset|download stalled/i.test(m)) {
      return "The proving files could not be downloaded. Check your connection and try again.";
    }
    // In-browser proof build ran out of memory or the prover crashed. This
    // step is heavy on memory and runs entirely on your device.
    if (/allocation failed|out of memory|memory access|\baborted\b|maximum call stack|witness/i.test(m)) {
      return "The private proof could not be built on your device. This step needs a lot of memory. Try again on a desktop browser with more free memory.";
    }
    // The local view of the pool did not match the chain yet.
    if (/out of sync|shielded pool state|moved on/i.test(m)) {
      return "The pool is still catching up to the chain. Wait a moment and try again.";
    }
    // The note we tried to spend is already spent on chain, usually because the
    // previous attempt actually went through. Resync rather than imply failure.
    if (/already spent/i.test(m)) {
      return "That note is already spent, so a previous attempt likely went through. Your balance has been refreshed, so check it before trying again.";
    }
    if (/InvalidExtDataHash|invalid ext data hash/i.test(m)) {
      return "The private transaction data did not match its proof. Reload the page and try again.";
    }
    // The browser-built proof did not verify against the pool's verifier. With
    // the served proving key confirmed to match the on-chain verifier, the
    // usual cause is a stale page: an old tab still running a prior build and
    // reading a prior cached key. Drop the cached proving data, then steer the
    // user to a full page reload, which both fetches the current build and
    // re-downloads a fresh key (a plain in-app balance refresh keeps the old
    // code). Fire and forget so the banner shows immediately.
    if (/did not verify/i.test(m)) {
      void clearProvingCache();
      return "The private proof did not verify. Your proving data has been reset. Reload this page to load the latest version, then refresh your balance and try again.";
    }
    if (/proof/i.test(m)) {
      return "The private proof did not verify. Refresh your balance and try again.";
    }
    const privateBalanceMatch = m.match(/private balance insufficient: available=(\d+); required=(\d+)/i);
    if (privateBalanceMatch) {
      const available = numU(BigInt(privateBalanceMatch[1]), 18, 4);
      const required = numU(BigInt(privateBalanceMatch[2]), 18, 4);
      return `Your private balance is ${available}, but this transaction needs ${required} including the relayer fee. Lower the amount or choose a cheaper relayer.`;
    }
    // The notes could not cover the amount in a single transaction.
    if (/two notes/i.test(m)) {
      return "This amount is split across too many notes. Receive into a single note or use a smaller amount.";
    }
    if (/relayer/i.test(m)) {
      return "The relayer could not forward this transaction. Try again in a moment.";
    }
  }
  // A network request failed before anything was sent.
  if (/failed to fetch|networkerror|load failed/i.test(m)) {
    return "A network request failed. Check your connection and try again.";
  }
  if (/insufficient/i.test(m)) return "Insufficient balance for this trade.";
  // An on-chain revert during simulation or send.
  if (/revert|invalidproof|invalidroot|invalidextdatahash|invalidpublicamount|duplicatecommitment|inputalreadyspent/i.test(m)) {
    return flow === "private"
      ? "The pool rejected this transaction on chain. Nothing was moved."
      : "The transaction reverted on chain. Nothing was changed.";
  }
  if (isBuy) {
    return "This buy could not go through, possibly a brief gap in liquidity. Try again in a moment.";
  }
  // Unrecognized cause: surface a short raw detail so an unexpected failure is
  // diagnosable from the banner itself, not only the console.
  const detail = m.replace(/\s+/g, " ").trim().slice(0, 200);
  return detail
    ? `The transaction could not be completed. Detail: ${detail}`
    : "The transaction could not be completed. Please try again.";
}

export default function Dapp() {
  // ---- body class ----
  useEffect(() => {
    document.body.className = "app";
    return () => {
      document.body.className = "";
    };
  }, []);

  // ---- view routing (mirrors the hash routing in the mock) ----
  const [matchTab, params] = useRoute("/app/:tab");
  const rawTab = (matchTab && params?.tab) || "trade";
  const view: View = (VIEWS as string[]).includes(rawTab) ? (rawTab as View) : "trade";
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [view]);

  // ---- wallet (real wagmi) ----
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const { switchChain } = useSwitchChain();
  const { openConnect } = useWalletUI();
  const wrongNet = isConnected && chainId !== mainnet.id;
  const rightNet = isConnected && !wrongNet;

  // ---- prewarm the proving key ----
  // Start the ~215MB proving-key download once the user has connected a wallet
  // on the correct network while on the Private tab, the only proving surface.
  // USD0 mint and redeem are plain public writes now, so the USD0 tab never
  // pulls proving assets; only shielding USD0 from the Private tab does. Gating
  // on rightNet means the download never starts for unauthenticated visitors or
  // search-engine crawlers — a page render alone is not enough to trigger it.
  // provingEnvBlock also skips phones, low-memory devices, and embedded frames
  // (none of which can prove), so no visitor is made to pull 215MB they cannot
  // use. Fired at most once per session; assets.ts no-ops when already cached.
  const prewarmedRef = useRef(false);
  useEffect(() => {
    if (prewarmedRef.current) return;
    if (view !== "private") return;
    if (!rightNet) return;
    if (!privatePoolConfigured("01") && !privatePoolConfigured("usd0")) return;
    if (provingEnvBlock() !== null) return;
    prewarmedRef.current = true;
    void prewarmProvingAssets();
  }, [view, rightNet]);

  // gate that every action button runs first: returns true if it
  // handled a connect / switch (so the caller should stop). A disconnected
  // click opens the wallet picker; a wrong-network click switches chains.
  const ensureReady = useCallback((): boolean => {
    if (!isConnected) {
      openConnect();
      return true;
    }
    if (wrongNet) {
      switchChain({ chainId: mainnet.id });
      return true;
    }
    return false;
  }, [isConnected, wrongNet, openConnect, switchChain]);

  const acctAddr = rightNet ? `${shortAddr(address as string)} · Ethereum mainnet` : "not connected";

  // ---- live on-chain stats (curve reads, refreshed) ----
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();

  const stats = useCurveStats();
  const market = useMarketStats();

  const spotNum = stats.spot !== undefined ? Number(formatUnits(stats.spot, 18)) : 0;

  // ---- wallet balances (account view) ----
  const balancesRead = useReadContracts({
    contracts:
      address && stats.token
        ? [
            { address: stats.token, abi: erc20Abi, functionName: "balanceOf", args: [address] },
            { address: USDS_ADDRESS, abi: erc20Abi, functionName: "balanceOf", args: [address] },
            { address: USDC_ADDRESS, abi: erc20Abi, functionName: "balanceOf", args: [address] },
            { address: USDT_ADDRESS, abi: erc20Abi, functionName: "balanceOf", args: [address] },
          ]
        : [],
    query: { enabled: Boolean(address && stats.token), refetchInterval: 15_000 },
  });
  const bal01 =
    balancesRead.data?.[0]?.status === "success" ? (balancesRead.data[0].result as bigint) : undefined;
  const balUsds =
    balancesRead.data?.[1]?.status === "success" ? (balancesRead.data[1].result as bigint) : undefined;
  const balUsdc =
    balancesRead.data?.[2]?.status === "success" ? (balancesRead.data[2].result as bigint) : undefined;
  const balUsdt =
    balancesRead.data?.[3]?.status === "success" ? (balancesRead.data[3].result as bigint) : undefined;

  // Native ETH balance for the connected wallet, shown in the header and used
  // as a pay asset in Trade.
  const { data: ethBalance } = useBalance({
    address,
    query: { enabled: Boolean(address), refetchInterval: 15_000 },
  });
  const balEth = ethBalance?.value;

  // ---- trade (live on-chain quotes against the curve) ----
  const [sellMode, setSellMode] = useState(false);
  // Once the curve is bonded, buys are closed at the contract, so open the
  // Trade tab on Sell. Initial state only: never override a choice the user
  // has made with the Buy/Sell toggle.
  const modeTouched = useRef(false);
  const bondedDefaultApplied = useRef(false);
  useEffect(() => {
    if (stats.bonded === true && !modeTouched.current && !bondedDefaultApplied.current) {
      bondedDefaultApplied.current = true;
      setSellMode(true);
    }
  }, [stats.bonded]);
  const [payAmt, setPayAmt] = useState("2");
  const [sell01, setSell01] = useState("1000"); // 01 in
  const [payAsset, setPayAsset] = useState<PayAsset>("ETH");
  const [outAsset, setOutAsset] = useState<SellAsset>("USDS");

  // The balance held in whichever asset is selected to pay with, plus its
  // decimals, so the Trade panel can show it and offer a Max fill.
  const payBalance = useMemo((): { v: bigint | undefined; dec: number } => {
    switch (payAsset) {
      case "USDS":
        return { v: balUsds, dec: 18 };
      case "USDC":
        return { v: balUsdc, dec: 6 };
      case "USDT":
        return { v: balUsdt, dec: 6 };
      case "ETH":
        return { v: balEth, dec: 18 };
    }
  }, [payAsset, balUsds, balUsdc, balUsdt, balEth]);

  const outBalance = outAsset === "USDS" ? balUsds : balUsdc;

  // Slippage tolerance, user-editable, default 1%. Applied to every
  // minimum-out value so a large trade is not sandwiched.
  const [slippagePct, setSlippagePct] = useState("1");
  const slipBps = useMemo(() => {
    const p = parseFloat(slippagePct);
    if (!isFinite(p) || p < 0) return 100n;
    return BigInt(Math.round(Math.min(p, 50) * 100));
  }, [slippagePct]);

  type Quote = { recv: string; price: string; burn: string; min: string; note?: string };
  const [quote, setQuote] = useState<Quote | null>(null);
  const [quoteErr, setQuoteErr] = useState<string | null>(null);

  // debounce the active amount so typing does not spam the RPC
  const activeAmt = sellMode ? sell01 : payAmt;
  const [debAmt, setDebAmt] = useState(activeAmt);
  useEffect(() => {
    const id = window.setTimeout(() => setDebAmt(activeAmt), 350);
    return () => window.clearTimeout(id);
  }, [activeAmt]);

  useEffect(() => {
    let cancelled = false;
    if (!publicClient) {
      setQuote(null);
      setQuoteErr(null);
      return;
    }
    (async () => {
      try {
        if (!sellMode) {
          // Issuing stops once the curve bonds, so no buy can be quoted.
          if (stats.bonded === true) {
            setQuote(null);
            setQuoteErr(null);
            return;
          }
          const dec = ASSET_DECIMALS[payAsset];
          const amt = parseDec(debAmt, dec);
          if (amt <= 0n) {
            setQuote(null);
            setQuoteErr(null);
            return;
          }
          // Convert the pay amount to the USDS terms the curve quotes in.
          let usds18: bigint;
          let note: string | undefined;
          let ethUsd = 0; // all-in USD value of the ETH being spent (ETH path)
          if (payAsset === "USDS") {
            usds18 = amt;
          } else if (payAsset === "USDC" || payAsset === "USDT") {
            usds18 = amt * E12; // stablecoins move roughly 1 to 1 into USDS
          } else {
            const rd = (await publicClient.readContract({
              address: ETH_USD_FEED,
              abi: aggregatorAbi,
              functionName: "latestRoundData",
            })) as readonly [bigint, bigint, bigint, bigint, bigint];
            const price8 = rd[1];
            if (price8 <= 0n) throw new Error("eth feed unavailable");
            usds18 = (amt * price8) / 10n ** 8n;
            ethUsd = Number(formatUnits(amt, 18)) * (Number(price8) / 1e8);
            note = "ETH price used " + usd(Number(price8) / 1e8, 2);
          }
          const res = (await publicClient.readContract({
            address: CURVE_ADDRESS,
            abi: curveAbi,
            functionName: "quoteBuy",
            args: [usds18],
          })) as readonly bigint[];
          const [coinOut, , burned, usdsUsed] = res;
          if (cancelled) return;
          // A buy that crosses the bond point is clamped by the curve: it
          // mints only the remaining 01 and uses only the USDS that costs,
          // which quoteBuy reports through usdsUsed. Every quote is a few wei
          // short of its input from rounding, so require a material unused
          // share (0.1%) before calling it a crossing. The refund behaviour
          // is the contract's documented rule (docs, "Bonding and the cap"):
          // the overpayment is refunded in the asset paid with.
          const unusedUsds = usds18 > usdsUsed ? usds18 - usdsUsed : 0n;
          if (unusedUsds * 1000n > usds18 && coinOut > 0n) {
            const crossNote =
              "This buy crosses the bond point. It mints only the remaining " +
              numU(coinOut, 18, 2) +
              " 01, uses about " +
              numU(usdsUsed, 18, 2) +
              " USDS of the payment, and the overpayment is refunded.";
            note = note ? note + " " + crossNote : crossNote;
          }
          // For ETH the curve quote above is priced off the Chainlink mid, which
          // ignores the swap fee and price impact, so it overstates the 01
          // received. Quote the real router execution and show that instead,
          // falling back to the mid estimate (marked by the "ETH price used"
          // note) only if the probe cannot run.
          let recvCoin = coinOut;
          let burnCoin = burned;
          let effPrice =
            coinOut > 0n
              ? Number(formatUnits(usdsUsed, 18)) / Number(formatUnits(coinOut, 18))
              : 0;
          if (payAsset === "ETH") {
            try {
              const probeAcct = (address ??
                "0x000000000000000000000000000000000000dEaD") as Address;
              const probe = await publicClient.simulateContract({
                address: ROUTER_ADDRESS,
                abi: routerAbi,
                functionName: "buyWithETH",
                args: [0n, 0n],
                account: probeAcct,
                value: amt,
                stateOverride: [{ address: probeAcct, balance: amt + 10n ** 18n }],
              });
              const realCoin = probe.result as bigint;
              if (realCoin > 0n) {
                recvCoin = realCoin;
                burnCoin = coinOut > 0n ? (burned * realCoin) / coinOut : burned;
                const recvF = Number(formatUnits(realCoin, 18));
                effPrice = recvF > 0 ? ethUsd / recvF : effPrice;
              }
            } catch {
              // keep the Chainlink-mid estimate; the note marks it as such
            }
            if (cancelled) return;
          }
          const minOut = applySlip(recvCoin, slipBps);
          setQuoteErr(null);
          setQuote({
            recv: numU(recvCoin, 18, 2),
            price: usd(effPrice),
            burn: numU(burnCoin, 18, 2) + " 01",
            min: numU(minOut, 18, 2) + " 01",
            note,
          });
        } else {
          const amt = parseDec(debAmt, 18);
          if (amt <= 0n) {
            setQuote(null);
            setQuoteErr(null);
            return;
          }
          const res = (await publicClient.readContract({
            address: CURVE_ADDRESS,
            abi: curveAbi,
            functionName: "quoteSell",
            args: [amt],
          })) as readonly bigint[];
          const [usdsOut, burned] = res;
          if (cancelled) return;
          const sold = Number(formatUnits(amt - burned, 18));
          const eff = sold > 0 ? Number(formatUnits(usdsOut, 18)) / sold : 0;
          setQuoteErr(null);
          if (outAsset === "USDS") {
            const minOut = applySlip(usdsOut, slipBps);
            setQuote({
              recv: numU(usdsOut, 18, 2),
              price: usd(eff),
              burn: numU(burned, 18, 2) + " 01",
              min: numU(minOut, 18, 2) + " USDS",
            });
          } else {
            const usdcOut = usdsOut / E12; // USDS moves roughly 1 to 1 into USDC
            const minOut = applySlip(usdcOut, slipBps);
            setQuote({
              recv: numU(usdcOut, 6, 2),
              price: usd(eff),
              burn: numU(burned, 18, 2) + " 01",
              min: numU(minOut, 6, 2) + " USDC",
            });
          }
        }
      } catch {
        if (cancelled) return;
        setQuote(null);
        setQuoteErr("This amount cannot be quoted right now.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debAmt, sellMode, payAsset, outAsset, slipBps, stats.bonded, publicClient, address]);

  const [tradeBanner, setTradeBanner] = useState<{ cls: string; text: ReactNode }>({
    cls: "banner info",
    text: null,
  });
  const [tradeBusy, setTradeBusy] = useState(false);

  const onTradeAction = useCallback(async () => {
    if (ensureReady()) return;
    if (!walletClient || !publicClient || !address) return;

    // Make sure an ERC20 spender is approved for at least `need`, simulating
    // and sending the approval first when it is short.
    const ensureAllowance = async (
      token: Address,
      spender: Address,
      need: bigint,
      label: string,
    ): Promise<void> => {
      const allowance = (await publicClient.readContract({
        address: token,
        abi: erc20Abi,
        functionName: "allowance",
        args: [address, spender],
      })) as bigint;
      if (allowance >= need) return;
      setTradeBanner({ cls: "banner info", text: `Approve ${label} in your wallet to continue.` });
      const { request } = await publicClient.simulateContract({
        address: token,
        abi: erc20Abi,
        functionName: "approve",
        args: [spender, need],
        account: address,
      });
      const aTx = await walletClient.writeContract(request);
      await publicClient.waitForTransactionReceipt({ hash: aTx });
    };

    setTradeBusy(true);
    try {
      if (!sellMode) {
        if (stats.bonded === true) {
          setTradeBanner({
            cls: "banner warn",
            text: "Issuing has reached the cap, so buys are closed. You can still sell.",
          });
          return;
        }
        const dec = ASSET_DECIMALS[payAsset];
        const amt = parseDec(payAmt, dec);
        if (amt <= 0n) {
          setTradeBanner({ cls: "banner warn", text: "Enter an amount first." });
          return;
        }

        // Quote the curve in USDS terms for a given USDS input, returning the
        // minimum 01 out after slippage.
        const minCoinFor = async (usds18: bigint): Promise<bigint> => {
          const q = (await publicClient.readContract({
            address: CURVE_ADDRESS,
            abi: curveAbi,
            functionName: "quoteBuy",
            args: [usds18],
          })) as readonly bigint[];
          return applySlip(q[0], slipBps);
        };

        setTradeBanner({ cls: "banner info", text: "Confirm the purchase in your wallet." });
        let tx: `0x${string}`;
        if (payAsset === "USDS") {
          const minCoinOut = await minCoinFor(amt);
          await ensureAllowance(USDS_ADDRESS, CURVE_ADDRESS, amt, "USDS");
          const { request } = await publicClient.simulateContract({
            address: CURVE_ADDRESS,
            abi: curveAbi,
            functionName: "buy",
            args: [amt, minCoinOut, address],
            account: address,
          });
          tx = await walletClient.writeContract(request);
        } else if (payAsset === "USDC") {
          const minCoinOut = await minCoinFor(amt * E12);
          await ensureAllowance(USDC_ADDRESS, ROUTER_ADDRESS, amt, "USDC");
          const { request } = await publicClient.simulateContract({
            address: ROUTER_ADDRESS,
            abi: routerAbi,
            functionName: "buyWithUSDC",
            args: [amt, minCoinOut],
            account: address,
          });
          tx = await walletClient.writeContract(request);
        } else if (payAsset === "USDT") {
          const minUsdcOut = applySlip(amt, slipBps); // USDT moves roughly 1 to 1 into USDC
          // Tie the curve floor to the worst-case USDC the swap can deliver.
          const minCoinOut = await minCoinFor(minUsdcOut * E12);
          await ensureAllowance(USDT_ADDRESS, ROUTER_ADDRESS, amt, "USDT");
          const { request } = await publicClient.simulateContract({
            address: ROUTER_ADDRESS,
            abi: routerAbi,
            functionName: "buyWithUSDT",
            args: [amt, minUsdcOut, minCoinOut],
            account: address,
          });
          tx = await walletClient.writeContract(request);
        } else {
          // Quote the real router execution (ETH to USDC to 01) right now, then
          // protect only the final 01 out. The previous floor was tied to the
          // Chainlink mid price, which ignores the swap fee and price impact, so
          // it reverted whenever the executable price gapped below the mid by
          // more than the slippage. minUsdcOut is left at 0 on purpose: fewer
          // USDC from a degraded swap yields fewer 01, which minCoinOut catches,
          // so the user's downside stays fully bounded.
          const probe = await publicClient.simulateContract({
            address: ROUTER_ADDRESS,
            abi: routerAbi,
            functionName: "buyWithETH",
            args: [0n, 0n],
            account: address,
            value: amt,
          });
          const minCoinOut = applySlip(probe.result as bigint, slipBps);
          const { request } = await publicClient.simulateContract({
            address: ROUTER_ADDRESS,
            abi: routerAbi,
            functionName: "buyWithETH",
            args: [0n, minCoinOut],
            account: address,
            value: amt,
          });
          tx = await walletClient.writeContract(request);
        }

        setTradeBanner({ cls: "banner info", text: txPending(tx) });
        const rcpt = await publicClient.waitForTransactionReceipt({ hash: tx });
        if (rcpt.status !== "success") {
          setTradeBanner({ cls: "banner warn", text: txDone("The buy was mined but reverted on chain. No 01 was received.", tx) });
          return;
        }
        setTradeBanner({ cls: "banner ok", text: txDone("Bought 01.", tx) });
      } else {
        const token = stats.token;
        if (!token) {
          setTradeBanner({
            cls: "banner warn",
            text: "The token address is still loading. Try again in a moment.",
          });
          return;
        }
        const amt = parseDec(sell01, 18);
        if (amt <= 0n) {
          setTradeBanner({ cls: "banner warn", text: "Enter an amount first." });
          return;
        }
        const q = (await publicClient.readContract({
          address: CURVE_ADDRESS,
          abi: curveAbi,
          functionName: "quoteSell",
          args: [amt],
        })) as readonly bigint[];
        const usdsOut = q[0];
        const minUsdsOut = applySlip(usdsOut, slipBps);

        setTradeBanner({ cls: "banner info", text: "Confirm the sale in your wallet." });
        let tx: `0x${string}`;
        if (outAsset === "USDS") {
          await ensureAllowance(token, CURVE_ADDRESS, amt, "01");
          const { request } = await publicClient.simulateContract({
            address: CURVE_ADDRESS,
            abi: curveAbi,
            functionName: "sell",
            args: [amt, minUsdsOut, address],
            account: address,
          });
          tx = await walletClient.writeContract(request);
        } else {
          const minUsdcOut = applySlip(usdsOut / E12, slipBps); // USDS moves roughly 1 to 1 into USDC
          await ensureAllowance(token, ROUTER_ADDRESS, amt, "01");
          const { request } = await publicClient.simulateContract({
            address: ROUTER_ADDRESS,
            abi: routerAbi,
            functionName: "sellForUSDC",
            args: [amt, minUsdsOut, minUsdcOut],
            account: address,
          });
          tx = await walletClient.writeContract(request);
        }

        setTradeBanner({ cls: "banner info", text: txPending(tx) });
        const rcpt = await publicClient.waitForTransactionReceipt({ hash: tx });
        if (rcpt.status !== "success") {
          setTradeBanner({ cls: "banner warn", text: txDone("The sale was mined but reverted on chain. No funds were received.", tx) });
          return;
        }
        setTradeBanner({ cls: "banner ok", text: txDone("Sold 01.", tx) });
      }
      stats.refetch();
      balancesRead.refetch();
    } catch (err) {
      setTradeBanner({ cls: "banner warn", text: humanError(err, { isBuy: !sellMode, flow: "trade" }) });
    } finally {
      setTradeBusy(false);
    }
  }, [
    ensureReady,
    walletClient,
    publicClient,
    address,
    sellMode,
    sell01,
    payAmt,
    payAsset,
    outAsset,
    slipBps,
    stats.token,
    stats.bonded,
    stats,
    balancesRead,
  ]);

  function tradeLabel(): string {
    if (!isConnected) return "Connect wallet";
    if (wrongNet) return "Switch to Ethereum";
    if (tradeBusy) return "Working…";
    if (!sellMode && stats.bonded === true) return "Buys closed";
    return sellMode ? "Sell 01" : "Buy 01";
  }
  const tradeDisabled = tradeBusy || (!sellMode && stats.bonded === true);
  // After the bond, curve buys revert at the contract, so the Buy tab points to
  // the secondary market (Uniswap) instead of showing a dead buy form.
  const buyClosed = stats.bonded === true && !sellMode;

  // ---- private pools ----
  const [privTab, setPrivTab] = useState<PrivTab>("shield");
  const [shieldAmt, setShieldAmt] = useState("");
  const [sendTo, setSendTo] = useState("");
  const [sendAmt, setSendAmt] = useState("");
  const [unAmt, setUnAmt] = useState("");
  const [unTo, setUnTo] = useState("");

  const { signMessageAsync } = useSignMessage();
  const [privBusy, setPrivBusy] = useState(false);
  const [privBanner, setPrivBanner] = useState<{ cls: string; text: ReactNode } | null>(null);
  const [privBalance, setPrivBalance] = useState<bigint | undefined>(undefined);
  const [shieldedAddr, setShieldedAddr] = useState<string | null>(null);
  const [relayerInfo, setRelayerInfo] = useState<AvailableRelayer | null>(null);
  const [relayerOptions, setRelayerOptions] = useState<AvailableRelayer[]>([]);
  const [customRelayerOpen, setCustomRelayerOpen] = useState(false);
  const [customRelayerUrl, setCustomRelayerUrl] = useState("");
  const [customRelayerBusy, setCustomRelayerBusy] = useState(false);
  const [customRelayerError, setCustomRelayerError] = useState<string | null>(null);

  // Which shielded pool the Private tab operates on. Both pools share one proof
  // pipeline (same verifier, hasher, and tree depth), so only the pool address,
  // the deposit token, and its symbol change. The 01 pool's deposit token is
  // read at runtime (stats.token); the USD0 pool's deposit token is the USD0
  // vault share.
  const [privPool, setPrivPool] = useState<PrivatePoolId>("01");
  const privPoolMeta = useMemo(
    () =>
      privPool === "usd0"
        ? { poolAddress: PRIVACY_CONFIG.pools.usd0, tokenAddress: PRIVACY_CONFIG.usd0, symbol: "USD0" }
        : { poolAddress: PRIVACY_CONFIG.pools["01"], tokenAddress: stats.token, symbol: "01" },
    [privPool, stats.token],
  );

  // the derived shielded account, cached per wallet address. The shielded
  // keypair is pool-independent, so it is reused across both pools.
  const shieldedKpRef = useRef<{ addr: string; kp: PrivateKeypair } | null>(null);

  // Load and validate every curated relayer against the canonical pool wiring.
  // The trusted Zero1 endpoint is the default; a user's choice is remembered
  // separately for 01 and USD0 because operators may quote different fees.
  useEffect(() => {
    let cancelled = false;
    if (!privatePoolConfigured(privPool)) {
      setRelayerInfo(null);
      setRelayerOptions([]);
      return;
    }
    loadRelayerRegistry()
      .then(async (listings) => {
        if (cancelled) return;
        const savedCustom = window.localStorage.getItem(`zero1:custom-relayer:${privPool}`);
        if (savedCustom) {
          try {
            const url = normalizeCustomRelayerUrl(savedCustom);
            listings.push({ id: "custom", name: "Custom relayer", url, trusted: false });
            setCustomRelayerUrl(url);
          } catch {
            window.localStorage.removeItem(`zero1:custom-relayer:${privPool}`);
          }
        } else {
          setCustomRelayerUrl("");
        }
        const results = await Promise.allSettled(
          listings.map(async (listing): Promise<AvailableRelayer> => ({
            ...listing,
            ...(await fetchPrivateRelayerStatus(privPool, listing.url)),
          })),
        );
        if (cancelled) return;
        const available = results
          .filter((result): result is PromiseFulfilledResult<AvailableRelayer> => result.status === "fulfilled")
          .map((result) => result.value);
        setRelayerOptions(available);
        const savedId = window.localStorage.getItem(`zero1:relayer:${privPool}`);
        const selected = available.find((option) => option.id === savedId)
          ?? available.find((option) => option.trusted)
          ?? available[0]
          ?? null;
        setRelayerInfo(selected);
      })
      .catch(() => {
        if (!cancelled) {
          setRelayerOptions([]);
          setRelayerInfo(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [view, privPool]);

  // a new wallet address means a different shielded account
  useEffect(() => {
    shieldedKpRef.current = null;
    setPrivBalance(undefined);
    setShieldedAddr(null);
    setPrivBanner(null);
  }, [address]);

  // Unlock the browser-encrypted key backup. The relayer stores only opaque
  // ciphertext and cannot derive or spend the user's notes.
  const ensureKeypair = useCallback(async (): Promise<PrivateKeypair> => {
    if (!address) throw new Error("connect a wallet first");
    const cached = shieldedKpRef.current;
    if (cached && cached.addr === address) return cached.kp;
    if (!PRIVACY_CONFIG.canonicalServiceUrl) throw new Error("canonical private service URL is not configured");
    const unlocked = await unlockOrCreateShieldedWallet({
      relayerUrl: PRIVACY_CONFIG.canonicalServiceUrl,
      signer: {
        getAddress: async () => address,
        signMessage: (message) => signMessageAsync({ message }),
      },
    });
    const kp = unlocked.keypair;
    shieldedKpRef.current = { addr: address, kp };
    setShieldedAddr(unlocked.shieldedAddress);
    return kp;
  }, [address, signMessageAsync]);

  // Recompute the private balance from on-chain state and the viewing key.
  const refreshPrivateBalance = useCallback(async (): Promise<void> => {
    const cached = shieldedKpRef.current;
    if (!cached || cached.addr !== address) return;
    const snapshot = await syncPrivatePool(privPool, cached.kp);
    setPrivBalance(await privatePoolBalance(snapshot));
  }, [address, privPool]);

  // Once the private account is unlocked, changing pools should refresh the
  // selected balance automatically. A pool switch must not leave an ambiguous
  // blank value that looks like a zero balance.
  useEffect(() => {
    const cached = shieldedKpRef.current;
    if (view !== "private" || !cached || cached.addr !== address) return;
    let cancelled = false;
    setPrivBalance(undefined);
    syncPrivatePool(privPool, cached.kp)
      .then(privatePoolBalance)
      .then((balance) => {
        if (!cancelled) setPrivBalance(balance);
      })
      .catch((err) => {
        if (!cancelled) setPrivBanner({ cls: "banner warn", text: humanError(err, { flow: "private" }) });
      });
    return () => {
      cancelled = true;
    };
  }, [address, privPool, view]);

  // Reveal the private balance: unlock keys, then scan.
  const onReveal = useCallback(async () => {
    if (ensureReady()) return;
    setPrivBusy(true);
    try {
      setPrivBanner({ cls: "banner info", text: "Sign in your wallet to unlock your viewing key." });
      await ensureKeypair();
      await refreshPrivateBalance();
      setPrivBanner(null);
    } catch (err) {
      setPrivBanner({ cls: "banner warn", text: humanError(err, { flow: "private" }) });
    } finally {
      setPrivBusy(false);
    }
  }, [ensureReady, ensureKeypair, refreshPrivateBalance]);

  const readFeeEnabled = useCallback(async (): Promise<boolean> => {
    if (!publicClient || !PRIVACY_CONFIG.feeController) throw new Error("fee controller is not configured");
    return await publicClient.readContract({
      address: PRIVACY_CONFIG.feeController,
      abi: feeControllerV2Abi,
      functionName: "feeEnabled",
    });
  }, [publicClient]);

  // Shielding is self-submitted because the pool pulls the public tokens from
  // msg.sender. The 0.3% protocol fee is deducted from the entered gross amount.
  const onShield = useCallback(async () => {
    if (ensureReady()) return;
    if (!walletClient || !publicClient || !address) return;
    const token = privPoolMeta.tokenAddress;
    const poolAddr = privPoolMeta.poolAddress;
    const sym = privPoolMeta.symbol;
    if (!token || !poolAddr || !privatePoolConfigured(privPool)) {
      setPrivBanner({ cls: "banner warn", text: "This pool is not configured yet." });
      return;
    }
    const amt = parseDec(shieldAmt, 18);
    if (amt <= 0n) {
      setPrivBanner({ cls: "banner warn", text: "Enter an amount first." });
      return;
    }
    const envBlock = provingEnvBlock();
    if (envBlock) {
      setPrivBanner(envBlock);
      return;
    }
    setPrivBusy(true);
    try {
      setPrivBanner({ cls: "banner info", text: "Sign in your wallet to unlock your private keys." });
      const kp = await ensureKeypair();
      setPrivBanner({ cls: "banner info", text: "Syncing private notes and fee state." });
      const [snapshot, feeEnabled] = await Promise.all([syncPrivatePool(privPool, kp), readFeeEnabled()]);
      setPrivBalance(await privatePoolBalance(snapshot));
      setPrivBanner(null);
      const prepared = await buildPrivateShield({
        pool: privPool, keypair: kp, amount: amt, snapshot, feeEnabled,
        onProgress: setProvingProgress,
      });
      setProvingProgress(null);
      const publicDebit = BigInt(prepared.ext.extAmount);

      // Verify the browser-built proof against the exact live wrapper before
      // asking for approval or opening a deposit transaction in the wallet.
      // This catches a stale proving-key cache without spending gas.
      const [liveVerifier, liveTreasuryPubkey] = await Promise.all([
        publicClient.readContract({
          address: poolAddr,
          abi: shieldedPoolV2Abi,
          functionName: "verifier",
        }),
        publicClient.readContract({
          address: poolAddr,
          abi: shieldedPoolV2Abi,
          functionName: "treasuryPubkey",
        }),
      ]);
      const proofVerified = await publicClient.readContract({
        address: liveVerifier,
        abi: verifierWrapperV2Abi,
        functionName: "verifyProof",
        args: [
          prepared.args.proof as `0x${string}`,
          [
            BigInt(prepared.args.root),
            BigInt(prepared.args.publicAmount),
            BigInt(prepared.args.extDataHash),
            1n,
            BigInt(prepared.args.publicFeeBase),
            feeEnabled ? 1n : 0n,
            liveTreasuryPubkey,
            BigInt(prepared.args.inputNullifiers[0]),
            BigInt(prepared.args.inputNullifiers[1]),
            BigInt(prepared.args.outputCommitments[0]),
            BigInt(prepared.args.outputCommitments[1]),
            BigInt(prepared.args.outputCommitments[2]),
          ],
        ],
      });
      if (!proofVerified) {
        await clearProvingCache();
        throw new Error("private proof did not verify against the deployed verifier");
      }

      const allowance = (await publicClient.readContract({
        address: token,
        abi: erc20Abi,
        functionName: "allowance",
        args: [address, poolAddr],
      })) as bigint;
      if (allowance < publicDebit) {
        setPrivBanner({ cls: "banner info", text: `Approve ${sym} for the pool in your wallet to continue.` });
        const { request } = await publicClient.simulateContract({
          address: token,
          abi: erc20Abi,
          functionName: "approve",
          args: [poolAddr, publicDebit],
          account: address,
        });
        const aTx = await walletClient.writeContract(request);
        await publicClient.waitForTransactionReceipt({ hash: aTx });
      }

      setPrivBanner({ cls: "banner info", text: "Confirm the deposit in your wallet." });
      const { request } = await publicClient.simulateContract({
        address: poolAddr,
        abi: shieldedPoolV2Abi,
        functionName: "transact",
        args: await toPrivateContractArgs(prepared),
        account: address,
      });
      const tx = await walletClient.writeContract(request);
      setPrivBanner({ cls: "banner info", text: txPending(tx) });
      const rcpt = await publicClient.waitForTransactionReceipt({ hash: tx });
      if (rcpt.status !== "success") {
        setPrivBanner({ cls: "banner warn", text: txDone("The deposit was mined but reverted on chain. Nothing was shielded.", tx) });
        return;
      }
      setPrivBanner({ cls: "banner info", text: txDone("Deposit confirmed. Waiting for the private balance index.", tx) });
      await waitForPrivatePoolIndex(privPool, rcpt.blockNumber);
      const privateReceived = amt - prepared.protocolFee;
      setPrivBanner({ cls: "banner ok", text: txDone(`Shielded ${numU(privateReceived, 18, 4)} ${sym}${prepared.protocolFee ? ` after a ${numU(prepared.protocolFee, 18, 4)} ${sym} protocol fee` : ""}.`, tx) });
      setShieldAmt("");
      await refreshPrivateBalance();
      balancesRead.refetch();
    } catch (err) {
      setPrivBanner({ cls: "banner warn", text: humanError(err, { flow: "private" }) });
    } finally {
      setProvingProgress(null);
      setPrivBusy(false);
    }
  }, [
    ensureReady,
    walletClient,
    publicClient,
    address,
    privPool,
    privPoolMeta,
    shieldAmt,
    ensureKeypair,
    readFeeEnabled,
    refreshPrivateBalance,
    balancesRead,
  ]);

  // Send: a private transfer to another shielded account. extAmount is zero
  // (nothing enters or leaves the pool); the relayer submits it for a fee.
  const onSend = useCallback(async () => {
    if (ensureReady()) return;
    if (!publicClient) return;
    if (!relayerInfo) {
      setPrivBanner({ cls: "banner warn", text: "No relayer is available right now, so private sends cannot be submitted." });
      return;
    }
    const amt = parseDec(sendAmt, 18);
    if (amt <= 0n) {
      setPrivBanner({ cls: "banner warn", text: "Enter an amount first." });
      return;
    }
    if (!sendTo.trim().startsWith("0x")) {
      setPrivBanner({ cls: "banner warn", text: "Enter the recipient's shielded address (starts with 0x)." });
      return;
    }
    const envBlock = provingEnvBlock();
    if (envBlock) {
      setPrivBanner(envBlock);
      return;
    }
    let relayerSubmissionStarted = false;
    setPrivBusy(true);
    try {
      setPrivBanner({ cls: "banner info", text: "Sign in your wallet to unlock your private keys." });
      const kp = await ensureKeypair();
      setPrivBanner({ cls: "banner info", text: "Syncing private notes and fee state." });
      const [snapshot, feeEnabled] = await Promise.all([syncPrivatePool(privPool, kp), readFeeEnabled()]);
      setPrivBalance(await privatePoolBalance(snapshot));
      setPrivBanner(null);
      const prepared = await buildPrivateTransfer({
        pool: privPool, keypair: kp, recipient: sendTo.trim(), amount: amt,
        snapshot, feeEnabled, relayer: relayerInfo.relayer, relayerFee: BigInt(relayerInfo.pool.minFee),
        onProgress: setProvingProgress,
      });
      setProvingProgress(null);
      setPrivBanner({ cls: "banner info", text: "Handing the proof to the relayer." });
      relayerSubmissionStarted = true;
      const tx = (await submitPrivateViaRelayer(privPool, prepared, relayerInfo.url)) as `0x${string}`;
      setPrivBanner({ cls: "banner info", text: txPending(tx) });
      let rcpt;
      try {
        rcpt = await publicClient.waitForTransactionReceipt({ hash: tx, timeout: 120_000 });
      } catch (waitErr) {
        // The relayer already broadcast the tx; our read RPC just cannot
        // confirm it yet. Do not imply failure, and do not hang on "Working".
        const timedOut = waitErr instanceof WaitForTransactionReceiptTimeoutError;
        setPrivBanner({
          cls: "banner info",
          text: txDone(
            timedOut
              ? "Submitted. Confirmation is taking longer than usual, so it may already be done. Check the link, then reveal your balance again."
              : "Submitted, but the confirmation could not be read just now. Check the link, then reveal your balance again.",
            tx,
          ),
        });
        setPrivBalance(undefined);
        return;
      }
      if (rcpt.status !== "success") {
        setPrivBanner({ cls: "banner warn", text: txDone("The transfer was mined but reverted on chain.", tx) });
        return;
      }
      setPrivBanner({ cls: "banner info", text: txDone("Transfer confirmed. Waiting for the private balance index.", tx) });
      await waitForPrivatePoolIndex(privPool, rcpt.blockNumber);
      setPrivBanner({ cls: "banner ok", text: txDone("Sent privately inside the pool.", tx) });
      setSendAmt("");
      await refreshPrivateBalance();
    } catch (err) {
      setPrivBanner({ cls: "banner warn", text: humanError(err, { flow: "private" }) });
      // Only hide notes when submission began and the relayer may have accepted
      // them. Local validation and proving failures cannot have spent a note.
      if (relayerSubmissionStarted) setPrivBalance(undefined);
    } finally {
      setProvingProgress(null);
      setPrivBusy(false);
    }
  }, [
    ensureReady,
    publicClient,
    relayerInfo,
    privPool,
    privPoolMeta,
    sendAmt,
    sendTo,
    ensureKeypair,
    readFeeEnabled,
    refreshPrivateBalance,
  ]);

  // Unshield: the entered amount is the gross private debit. extAmount is the
  // negative public payout after the 0.3% protocol fee.
  const onUnshield = useCallback(async () => {
    if (ensureReady()) return;
    if (!publicClient) return;
    if (!relayerInfo) {
      setPrivBanner({ cls: "banner warn", text: "No relayer is available right now, so withdrawals cannot be submitted." });
      return;
    }
    const amt = parseDec(unAmt, 18);
    if (amt <= 0n) {
      setPrivBanner({ cls: "banner warn", text: "Enter an amount first." });
      return;
    }
    if (!/^0x[0-9a-fA-F]{40}$/.test(unTo.trim())) {
      setPrivBanner({ cls: "banner warn", text: "Enter a valid destination address (0x and 40 hex characters)." });
      return;
    }
    const envBlock = provingEnvBlock();
    if (envBlock) {
      setPrivBanner(envBlock);
      return;
    }
    let relayerSubmissionStarted = false;
    setPrivBusy(true);
    try {
      setPrivBanner({ cls: "banner info", text: "Sign in your wallet to unlock your private keys." });
      const kp = await ensureKeypair();
      setPrivBanner({ cls: "banner info", text: "Syncing private notes and fee state." });
      const [snapshot, feeEnabled] = await Promise.all([syncPrivatePool(privPool, kp), readFeeEnabled()]);
      setPrivBalance(await privatePoolBalance(snapshot));
      setPrivBanner(null);
      const prepared = await buildPrivateUnshield({
        pool: privPool, keypair: kp, recipient: unTo.trim() as Address, amount: amt,
        snapshot, feeEnabled, relayer: relayerInfo.relayer, relayerFee: BigInt(relayerInfo.pool.minFee),
        onProgress: setProvingProgress,
      });
      setProvingProgress(null);
      setPrivBanner({ cls: "banner info", text: "Handing the proof to the relayer." });
      relayerSubmissionStarted = true;
      const tx = (await submitPrivateViaRelayer(privPool, prepared, relayerInfo.url)) as `0x${string}`;
      setPrivBanner({ cls: "banner info", text: txPending(tx) });
      let rcpt;
      try {
        rcpt = await publicClient.waitForTransactionReceipt({ hash: tx, timeout: 120_000 });
      } catch (waitErr) {
        // The relayer already broadcast the tx; our read RPC just cannot
        // confirm it yet. Do not imply failure, and do not hang on "Working".
        const timedOut = waitErr instanceof WaitForTransactionReceiptTimeoutError;
        setPrivBanner({
          cls: "banner info",
          text: txDone(
            timedOut
              ? "Submitted. Confirmation is taking longer than usual, so it may already be done. Check the link, then reveal your balance again."
              : "Submitted, but the confirmation could not be read just now. Check the link, then reveal your balance again.",
            tx,
          ),
        });
        setPrivBalance(undefined);
        return;
      }
      if (rcpt.status !== "success") {
        setPrivBanner({ cls: "banner warn", text: txDone("The withdrawal was mined but reverted on chain.", tx) });
        return;
      }
      setPrivBanner({ cls: "banner info", text: txDone("Withdrawal confirmed. Waiting for the private balance index.", tx) });
      await waitForPrivatePoolIndex(privPool, rcpt.blockNumber);
      const publicReceived = -BigInt(prepared.ext.extAmount);
      setPrivBanner({ cls: "banner ok", text: txDone(`Withdrew ${numU(publicReceived, 18, 4)} ${privPoolMeta.symbol} after the protocol fee.`, tx) });
      setUnAmt("");
      await refreshPrivateBalance();
      balancesRead.refetch();
    } catch (err) {
      setPrivBanner({ cls: "banner warn", text: humanError(err, { flow: "private" }) });
      // Only hide notes when submission began and the relayer may have accepted
      // them. Local validation and proving failures cannot have spent a note.
      if (relayerSubmissionStarted) setPrivBalance(undefined);
    } finally {
      setProvingProgress(null);
      setPrivBusy(false);
    }
  }, [
    ensureReady,
    publicClient,
    relayerInfo,
    privPool,
    privPoolMeta,
    unAmt,
    unTo,
    ensureKeypair,
    readFeeEnabled,
    refreshPrivateBalance,
    balancesRead,
  ]);

  // Switching pools drops the previous pool's private state so nothing from one
  // pool leaks into the other view. The relayer poll re-runs from its effect
  // (which depends on privPool) to re-price the fee in the new pool's token.
  const onSwitchPrivPool = useCallback(
    (next: PrivatePoolId) => {
      if (next === privPool || privBusy) return;
      setPrivPool(next);
      setPrivBalance(undefined);
      setPrivBanner(null);
      setProvingProgress(null);
      setRelayerInfo(null);
      setRelayerOptions([]);
      setCustomRelayerOpen(false);
      setCustomRelayerError(null);
    },
    [privPool, privBusy],
  );

  const onSelectRelayer = useCallback((id: string) => {
    if (id === "__custom__") {
      if (!privBusy) {
        setCustomRelayerOpen(true);
        setCustomRelayerError(null);
      }
      return;
    }
    const selected = relayerOptions.find((option) => option.id === id);
    if (!selected || privBusy) return;
    setRelayerInfo(selected);
    setCustomRelayerOpen(false);
    setCustomRelayerError(null);
    window.localStorage.setItem(`zero1:relayer:${privPool}`, selected.id);
  }, [privPool, privBusy, relayerOptions]);

  const onUseCustomRelayer = useCallback(async () => {
    if (privBusy || customRelayerBusy) return;
    setCustomRelayerBusy(true);
    setCustomRelayerError(null);
    try {
      const url = normalizeCustomRelayerUrl(customRelayerUrl.trim());
      const status = await fetchPrivateRelayerStatus(privPool, url);
      const custom: AvailableRelayer = {
        id: "custom",
        name: "Custom relayer",
        url,
        trusted: false,
        ...status,
      };
      setRelayerOptions((current) => [...current.filter((option) => option.id !== "custom"), custom]);
      setRelayerInfo(custom);
      setCustomRelayerUrl(url);
      setCustomRelayerOpen(false);
      window.localStorage.setItem(`zero1:custom-relayer:${privPool}`, url);
      window.localStorage.setItem(`zero1:relayer:${privPool}`, "custom");
    } catch (error) {
      setCustomRelayerError(error instanceof Error ? error.message : "Custom relayer validation failed");
    } finally {
      setCustomRelayerBusy(false);
    }
  }, [customRelayerBusy, customRelayerUrl, privBusy, privPool]);

  const onRemoveCustomRelayer = useCallback(() => {
    if (privBusy || customRelayerBusy) return;
    const remaining = relayerOptions.filter((option) => option.id !== "custom");
    const fallback = remaining.find((option) => option.trusted) ?? remaining[0] ?? null;
    setRelayerOptions(remaining);
    setRelayerInfo(fallback);
    setCustomRelayerUrl("");
    setCustomRelayerOpen(false);
    setCustomRelayerError(null);
    window.localStorage.removeItem(`zero1:custom-relayer:${privPool}`);
    if (fallback) window.localStorage.setItem(`zero1:relayer:${privPool}`, fallback.id);
    else window.localStorage.removeItem(`zero1:relayer:${privPool}`);
  }, [customRelayerBusy, privBusy, privPool, relayerOptions]);

  function privLabel(base: PrivTab): string {
    if (!isConnected) return "Connect wallet";
    if (wrongNet) return "Switch to Ethereum";
    if (privBusy) return "Working…";
    return { shield: `Shield ${privPoolMeta.symbol}`, send: "Send privately", unshield: "Withdraw" }[base];
  }

  // Relayer fee shown next to send/withdraw, denominated in the selected pool's
  // token. "—" until the relayer reports it.
  const relayerFeeText =
    relayerInfo?.pool.minFee
      ? `${fmtUnits(BigInt(relayerInfo.pool.minFee), 4)} ${privPoolMeta.symbol}`
      : "—";
  const selectedRelayerFee = relayerInfo ? BigInt(relayerInfo.pool.minFee) : 0n;
  const withdrawalGross = parseDec(unAmt, 18);
  const withdrawalPrivateSpend = withdrawalGross + selectedRelayerFee;

  // ---- borrow / lender pool (real ShieldedLending) ----
  const [supplyAmt, setSupplyAmt] = useState("1000");
  const [collAmt, setCollAmt] = useState("10000");
  const [borrowAmt, setBorrowAmt] = useState("120");
  const [repayAmt, setRepayAmt] = useState("");

  const SECONDS_PER_YEAR = 31_536_000;
  const WAD = 10n ** 18n;
  // Virtual offsets baked into the verified ShieldedLending (constants):
  // VIRTUAL_SHARES = 1e6, VIRTUAL_ASSETS = 1. They guard the share price
  // against a first-deposit donation attack and are part of every
  // shares<->USDS conversion.
  const VIRTUAL_SHARES = 1_000_000n;
  const VIRTUAL_ASSETS = 1n;

  // Pool-wide params and the connected supplier's position, refreshed on
  // an interval like the rest of the dapp. The lender holds its idle
  // backing as sUSDS, so we also read its sUSDS balance to value the pool.
  const lendingRead = useReadContracts({
    contracts: [
      { address: SHIELDED_LENDING_ADDRESS, abi: shieldedLendingAbi, functionName: "LTV" },
      { address: SHIELDED_LENDING_ADDRESS, abi: shieldedLendingAbi, functionName: "BPS" },
      { address: SHIELDED_LENDING_ADDRESS, abi: shieldedLendingAbi, functionName: "RATE_PER_SEC" },
      { address: SHIELDED_LENDING_ADDRESS, abi: shieldedLendingAbi, functionName: "LIQ_THRESHOLD" },
      { address: SHIELDED_LENDING_ADDRESS, abi: shieldedLendingAbi, functionName: "priceAvg" },
      { address: SHIELDED_LENDING_ADDRESS, abi: shieldedLendingAbi, functionName: "totalShares" },
      { address: SHIELDED_LENDING_ADDRESS, abi: shieldedLendingAbi, functionName: "totalScaledDebt" },
      { address: SHIELDED_LENDING_ADDRESS, abi: shieldedLendingAbi, functionName: "borrowIndex" },
      { address: SHIELDED_LENDING_ADDRESS, abi: shieldedLendingAbi, functionName: "lastAccrual" },
      { address: SUSDS_ADDRESS, abi: sUsdsAbi, functionName: "balanceOf", args: [SHIELDED_LENDING_ADDRESS] },
      { address: SHIELDED_LENDING_ADDRESS, abi: shieldedLendingAbi, functionName: "shares", args: [address ?? zeroAddress] },
      { address: SHIELDED_LENDING_ADDRESS, abi: shieldedLendingAbi, functionName: "CLOSE_FACTOR" },
      { address: SHIELDED_LENDING_ADDRESS, abi: shieldedLendingAbi, functionName: "LIQ_BONUS" },
    ],
    query: { enabled: false },
  });

  const L = useMemo(() => {
    const d = lendingRead.data;
    const big = (i: number) =>
      d && d[i]?.status === "success" ? (d[i].result as bigint) : undefined;
    return {
      ltv: big(0),
      bps: big(1),
      ratePerSec: big(2),
      liqThreshold: big(3),
      priceAvg: big(4),
      totalShares: big(5),
      totalScaledDebt: big(6),
      borrowIndex: big(7),
      lastAccrual: big(8),
      sUsdsBal: big(9),
      userShares: address ? big(10) : 0n,
      closeFactor: big(11),
      liqBonus: big(12),
    };
  }, [lendingRead.data, address]);

  // The pool's idle USDS is held as sUSDS; convert that balance to USDS.
  const tvlRead = useReadContracts({
    contracts:
      L.sUsdsBal !== undefined
        ? [{ address: SUSDS_ADDRESS, abi: sUsdsAbi, functionName: "convertToAssets", args: [L.sUsdsBal] }]
        : [],
    query: { enabled: false },
  });
  const idleAssets =
    tvlRead.data?.[0]?.status === "success" ? (tvlRead.data[0].result as bigint) : undefined;

  // Total value of the pool = idle USDS (from sUSDS) + outstanding debt,
  // mirroring ShieldedLending._tvl(). Debt uses the index accrued to now,
  // matching _currentIndex(), so the position figure does not lag.
  const tvl = useMemo(() => {
    if (idleAssets === undefined || L.totalScaledDebt === undefined || L.borrowIndex === undefined) {
      return undefined;
    }
    let idx = L.borrowIndex;
    if (L.totalScaledDebt > 0n && L.lastAccrual !== undefined && L.ratePerSec !== undefined) {
      const dt = BigInt(Math.floor(Date.now() / 1000)) - L.lastAccrual;
      if (dt > 0n) idx = L.borrowIndex + (L.borrowIndex * L.ratePerSec * dt) / WAD;
    }
    return idleAssets + (L.totalScaledDebt * idx) / WAD;
  }, [idleAssets, L.totalScaledDebt, L.borrowIndex, L.lastAccrual, L.ratePerSec]);

  // The connected supplier's position in USDS, from their shares and TVL.
  const userPositionUsds = useMemo(() => {
    if (tvl === undefined || L.totalShares === undefined || L.userShares === undefined) return undefined;
    return (L.userShares * (tvl + VIRTUAL_ASSETS)) / (L.totalShares + VIRTUAL_SHARES);
  }, [tvl, L.totalShares, L.userShares]);

  // Borrow APR from the contract's per-second rate (1e18-scaled).
  const borrowApr = useMemo(() => {
    if (L.ratePerSec === undefined) return undefined;
    return (Number(L.ratePerSec) * SECONDS_PER_YEAR) / 1e18 * 100;
  }, [L.ratePerSec]);
  const rateStr = borrowApr !== undefined ? borrowApr.toFixed(2) + "% APR" : "—";

  const positionStr = !rightNet
    ? "—"
    : userPositionUsds !== undefined
      ? numU(userPositionUsds, 18, 2) + " USDS"
      : "loading…";

  // ---- USD0 value-accruing share token ----
  const [usd0MintAmt, setUsd0MintAmt] = useState("1000");
  const [usd0RedeemAmt, setUsd0RedeemAmt] = useState("1000");
  const [usd0Mode, setUsd0Mode] = useState<"mint" | "redeem">("mint");
  const [usd0Busy, setUsd0Busy] = useState(false);
  const [usd0Banner, setUsd0Banner] = useState<{ cls: string; text: ReactNode } | null>(null);

  const usd0Read = useReadContracts({
    contracts: [
      { address: PRIVACY_CONFIG.usd0 ?? zeroAddress, abi: usd0V2Abi, functionName: "totalAssets" },
      { address: PRIVACY_CONFIG.usd0 ?? zeroAddress, abi: usd0V2Abi, functionName: "totalSupply" },
      { address: PRIVACY_CONFIG.usd0 ?? zeroAddress, abi: usd0V2Abi, functionName: "managedAssets" },
      { address: PRIVACY_CONFIG.usd0 ?? zeroAddress, abi: usd0V2Abi, functionName: "balanceOf", args: [address ?? zeroAddress] },
      { address: PRIVACY_CONFIG.usd0 ?? zeroAddress, abi: usd0V2Abi, functionName: "convertToShares", args: [parseDec(usd0MintAmt, 6) * E12] },
      { address: PRIVACY_CONFIG.usd0 ?? zeroAddress, abi: usd0V2Abi, functionName: "convertToAssets", args: [parseDec(usd0RedeemAmt, 18)] },
    ],
    query: { enabled: usd0PrivacyConfigured, refetchInterval: 15_000 },
  });

  const U = useMemo(() => {
    const d = usd0Read.data;
    const big = (i: number) =>
      d && d[i]?.status === "success" ? (d[i].result as bigint) : undefined;
    return {
      totalAssets: big(0),
      totalSupply: big(1),
      managedAssets: big(2),
      bal: address ? big(3) : 0n,
      mintOut: big(4),
      redeemOut: big(5),
    };
  }, [usd0Read.data, address]);

  const usd0TvlStr =
    U.totalAssets !== undefined ? usd2(Number(formatUnits(U.totalAssets, 18))) : "—";
  const usd0SupplyStr =
    U.totalSupply !== undefined ? num(Number(formatUnits(U.totalSupply, 18)), 2) + " USD0" : "—";
  const usd0BackingStr = U.totalSupply && U.managedAssets !== undefined
    ? `${num(Number(formatUnits((U.managedAssets * 10n ** 18n) / U.totalSupply, 18)), 6)} USDS`
    : "—";
  const usd0HolderReserveStr =
    U.managedAssets !== undefined ? usd2(Number(formatUnits(U.managedAssets, 18))) : "—";
  const usd0WalletStr =
    U.bal !== undefined ? num(Number(formatUnits(U.bal, 18)), 2) + " USD0" : "—";

  // The wallet balance of the token the active Private pool shields, used by the
  // Shield Max button and balance tag.
  const privTokenBal = privPool === "usd0" ? U.bal : bal01;

  const usd0MintOut = U.mintOut !== undefined ? Number(formatUnits(U.mintOut, 18)) : undefined;
  const usd0RedeemOut = U.redeemOut !== undefined ? Number(formatUnits(U.redeemOut, 18)) : undefined;
  const usd0RedeemMin = usd0RedeemOut !== undefined ? usd0RedeemOut * 0.995 : undefined;

  // Mint USD0: deposit public USDS into the vault, which issues USD0 one for
  // one straight to the wallet. A plain public write: approve USDS if needed,
  // then mint. No proof, no relayer, no fee. To make the USD0 private, shield
  // it afterwards from the Private tab.
  const runUsd0Mint = useCallback(async () => {
    if (ensureReady()) return;
    if (!walletClient || !publicClient || !address) return;
    if (!PRIVACY_CONFIG.usd0 || !PRIVACY_CONFIG.usd0Gateway) {
      setUsd0Banner({ cls: "banner warn", text: "USD0 gateway is not configured yet." });
      return;
    }
    const usdcAmount = parseDec(usd0MintAmt, 6);
    if (usdcAmount <= 0n) {
      setUsd0Banner({ cls: "banner warn", text: "Enter how much USDC to deposit." });
      return;
    }
    setUsd0Busy(true);
    try {
      const allowance = (await publicClient.readContract({
        address: USDC_ADDRESS,
        abi: erc20Abi,
        functionName: "allowance",
        args: [address, PRIVACY_CONFIG.usd0Gateway],
      })) as bigint;
      if (allowance < usdcAmount) {
        setUsd0Banner({ cls: "banner info", text: "Approve USDC for the gateway in your wallet to continue." });
        const { request } = await publicClient.simulateContract({
          address: USDC_ADDRESS,
          abi: erc20Abi,
          functionName: "approve",
          args: [PRIVACY_CONFIG.usd0Gateway, usdcAmount],
          account: address,
        });
        const aTx = await walletClient.writeContract(request);
        await publicClient.waitForTransactionReceipt({ hash: aTx });
      }

      const quotedShares = (await publicClient.readContract({
        address: PRIVACY_CONFIG.usd0,
        abi: usd0V2Abi,
        functionName: "convertToShares",
        args: [usdcAmount * E12],
      })) as bigint;
      setUsd0Banner({ cls: "banner info", text: "Confirm the mint in your wallet." });
      const { request } = await publicClient.simulateContract({
        address: PRIVACY_CONFIG.usd0Gateway,
        abi: usd0GatewayAbi,
        functionName: "mintWithUSDC",
        args: [usdcAmount, applySlip(quotedShares, 50n), address, BigInt(Math.floor(Date.now() / 1000) + 20 * 60)],
        account: address,
      });
      const tx = await walletClient.writeContract(request);
      setUsd0Banner({ cls: "banner info", text: txPending(tx) });
      const rcpt = await publicClient.waitForTransactionReceipt({ hash: tx });
      if (rcpt.status !== "success") {
        setUsd0Banner({ cls: "banner warn", text: txDone("The mint was mined but reverted on chain. No USD0 was minted.", tx) });
        return;
      }
      setUsd0Banner({ cls: "banner ok", text: txDone("Minted USD0 to your wallet.", tx) });
      setUsd0MintAmt("");
      usd0Read.refetch();
      balancesRead.refetch();
    } catch (err) {
      setUsd0Banner({ cls: "banner warn", text: humanError(err, { flow: "trade" }) });
    } finally {
      setUsd0Busy(false);
    }
  }, [
    ensureReady,
    walletClient,
    publicClient,
    address,
    usd0MintAmt,
    U.mintOut,
    usd0Read,
    balancesRead,
  ]);

  // Redeem USD0: burn public USD0 from the wallet and pull USDS back one for
  // one. A plain public write (no proof, no relayer, no approve, since the
  // vault burns the caller's own USD0). The floor is the full amount because
  // the vault redeems one for one; a shortfall reverts rather than paying less.
  const runUsd0Redeem = useCallback(async () => {
    if (ensureReady()) return;
    if (!walletClient || !publicClient || !address) return;
    if (!PRIVACY_CONFIG.usd0 || !PRIVACY_CONFIG.usd0Gateway) {
      setUsd0Banner({ cls: "banner warn", text: "USD0 gateway is not configured yet." });
      return;
    }
    const usd0Amount = parseDec(usd0RedeemAmt, 18);
    if (usd0Amount <= 0n) {
      setUsd0Banner({ cls: "banner warn", text: "Enter how much USD0 to redeem." });
      return;
    }
    setUsd0Busy(true);
    try {
      const quotedUsds = (await publicClient.readContract({
        address: PRIVACY_CONFIG.usd0,
        abi: usd0V2Abi,
        functionName: "convertToAssets",
        args: [usd0Amount],
      })) as bigint;
      const quotedUsdc = (await publicClient.readContract({
        address: PRIVACY_CONFIG.usd0Gateway,
        abi: usd0GatewayAbi,
        functionName: "previewUsdcOut",
        args: [quotedUsds],
      })) as bigint;
      const allowance = (await publicClient.readContract({
        address: PRIVACY_CONFIG.usd0,
        abi: erc20Abi,
        functionName: "allowance",
        args: [address, PRIVACY_CONFIG.usd0Gateway],
      })) as bigint;
      if (allowance < usd0Amount) {
        setUsd0Banner({ cls: "banner info", text: "Approve USD0 for the gateway in your wallet to continue." });
        const { request } = await publicClient.simulateContract({
          address: PRIVACY_CONFIG.usd0,
          abi: erc20Abi,
          functionName: "approve",
          args: [PRIVACY_CONFIG.usd0Gateway, usd0Amount],
          account: address,
        });
        const approvalTx = await walletClient.writeContract(request);
        await publicClient.waitForTransactionReceipt({ hash: approvalTx });
      }
      setUsd0Banner({ cls: "banner info", text: "Confirm the redemption in your wallet." });
      const { request } = await publicClient.simulateContract({
        address: PRIVACY_CONFIG.usd0Gateway,
        abi: usd0GatewayAbi,
        functionName: "redeemToUSDC",
        args: [usd0Amount, applySlip(quotedUsdc, 50n), address, BigInt(Math.floor(Date.now() / 1000) + 20 * 60)],
        account: address,
      });
      const tx = await walletClient.writeContract(request);
      setUsd0Banner({ cls: "banner info", text: txPending(tx) });
      const rcpt = await publicClient.waitForTransactionReceipt({ hash: tx });
      if (rcpt.status !== "success") {
        setUsd0Banner({ cls: "banner warn", text: txDone("The redemption was mined but reverted on chain. No USDC was received.", tx) });
        return;
      }
      setUsd0Banner({ cls: "banner ok", text: txDone("Redeemed USD0 to USDC.", tx) });
      setUsd0RedeemAmt("");
      usd0Read.refetch();
      balancesRead.refetch();
    } catch (err) {
      setUsd0Banner({ cls: "banner warn", text: humanError(err, { flow: "trade" }) });
    } finally {
      setUsd0Busy(false);
    }
  }, [
    ensureReady,
    walletClient,
    publicClient,
    address,
    usd0RedeemAmt,
    usd0Read,
    balancesRead,
  ]);

  // ---- account summary (read-only derivations for the Account view) ----
  // Every figure here comes from a real on-chain read or a browser-computed
  // private balance. 01 is valued at the live curve price; ETH is shown as a
  // raw balance because the dapp has no top-level ETH/USD price, so it is
  // deliberately left out of any USD total rather than guessed at.
  const acct = useMemo(() => {
    const n = (v: bigint | undefined, dec: number) =>
      v === undefined ? undefined : Number(formatUnits(v, dec));
    const pub01 = n(bal01, 18);
    const priv01 = privBalance !== undefined ? Number(formatUnits(privBalance, 18)) : undefined;
    const known01 = pub01;
    const value01 = known01 === undefined ? undefined : known01 * spotNum;
    const usds = n(balUsds, 18);
    const usdc = n(balUsdc, 6);
    const usdt = n(balUsdt, 6);
    const stables =
      usds !== undefined && usdc !== undefined && usdt !== undefined ? usds + usdc + usdt : undefined;
    const supplied =
      userPositionUsds !== undefined ? Number(formatUnits(userPositionUsds, 18)) : undefined;
    // Public vs private split of your 01, only when the private side is known.
    const split =
      pub01 !== undefined && priv01 !== undefined && pub01 + priv01 > 0
        ? {
            pubShare: (pub01 / (pub01 + priv01)) * 100,
            privShare: (priv01 / (pub01 + priv01)) * 100,
          }
        : null;
    // Your share of the lender pool, from your position over pool TVL.
    const poolShare =
      userPositionUsds !== undefined && tvl !== undefined && tvl > 0n
        ? Number((userPositionUsds * 10000n) / tvl) / 100
        : undefined;
    return { pub01, priv01, known01, value01, usds, usdc, usdt, stables, supplied, split, poolShare };
  }, [bal01, privBalance, balUsds, balUsdc, balUsdt, userPositionUsds, tvl, spotNum]);

  // Real collateral valuation for the entered 01 amount, debounced.
  const [debColl, setDebColl] = useState(collAmt);
  useEffect(() => {
    const id = window.setTimeout(() => setDebColl(collAmt), 350);
    return () => window.clearTimeout(id);
  }, [collAmt]);
  const collParsed = useMemo(() => parseDec(debColl, 18), [debColl]);
  const collValRead = useReadContracts({
    contracts:
      collParsed > 0n
        ? [{ address: SHIELDED_LENDING_ADDRESS, abi: shieldedLendingAbi, functionName: "collateralValue", args: [collParsed] }]
        : [],
    query: { enabled: false },
  });
  const collVal =
    collValRead.data?.[0]?.status === "success" ? (collValRead.data[0].result as bigint) : undefined;

  // Borrow figures from the real reads. collateralValue already applies
  // the haircut and the conservative time-averaged price. Loan health is
  // projected for the loan you would open: the liquidation ceiling over
  // the entered debt (higher is safer, matching the docs convention).
  const borrow = useMemo(() => {
    const bps = L.bps ?? 10_000n;
    const maxBorrowBig =
      collVal !== undefined && L.ltv !== undefined ? (collVal * L.ltv) / bps : undefined;
    const ceilingBig =
      collVal !== undefined && L.liqThreshold !== undefined ? (collVal * L.liqThreshold) / bps : undefined;
    const b = parseFloat(borrowAmt) || 0;
    let healthNum = "∞";
    let pct = 100;
    let cls = "health";
    if (ceilingBig !== undefined && b > 0) {
      const health = Number(formatUnits(ceilingBig, 18)) / b;
      healthNum = health > 9 ? "∞" : health.toFixed(2);
      pct = Math.max(4, Math.min(100, (health / 2.5) * 100));
      cls = "health" + (health < 1.15 ? " low" : health < 1.6 ? " mid" : "");
    }
    return {
      collVal: collVal !== undefined ? usd(Number(formatUnits(collVal, 18)), 0) : "—",
      maxBorrow: maxBorrowBig !== undefined ? usd(Number(formatUnits(maxBorrowBig, 18)), 0) : "—",
      interest: rateStr,
      healthNum,
      pct,
      cls,
    };
  }, [collVal, L.ltv, L.bps, L.liqThreshold, borrowAmt, rateStr]);

  // ---- supply / withdraw writes (plain USDS in and out) ----
  const [supplyBusy, setSupplyBusy] = useState(false);
  const [supplyBanner, setSupplyBanner] = useState<{ cls: string; text: ReactNode } | null>(null);

  const runSupply = useCallback(async () => {
    if (ensureReady()) return;
    if (!walletClient || !publicClient || !address) return;
    const amt = parseDec(supplyAmt, 18);
    if (amt <= 0n) {
      setSupplyBanner({ cls: "banner warn", text: "Enter an amount first." });
      return;
    }
    setSupplyBusy(true);
    try {
      const allowance = (await publicClient.readContract({
        address: USDS_ADDRESS,
        abi: erc20Abi,
        functionName: "allowance",
        args: [address, SHIELDED_LENDING_ADDRESS],
      })) as bigint;
      if (allowance < amt) {
        setSupplyBanner({ cls: "banner info", text: "Approve USDS in your wallet to continue." });
        const { request } = await publicClient.simulateContract({
          address: USDS_ADDRESS,
          abi: erc20Abi,
          functionName: "approve",
          args: [SHIELDED_LENDING_ADDRESS, amt],
          account: address,
        });
        const aTx = await walletClient.writeContract(request);
        await publicClient.waitForTransactionReceipt({ hash: aTx });
      }
      setSupplyBanner({ cls: "banner info", text: "Confirm the supply in your wallet." });
      const { request } = await publicClient.simulateContract({
        address: SHIELDED_LENDING_ADDRESS,
        abi: shieldedLendingAbi,
        functionName: "supply",
        args: [amt],
        account: address,
      });
      const tx = await walletClient.writeContract(request);
      setSupplyBanner({ cls: "banner info", text: txPending(tx) });
      const rcpt = await publicClient.waitForTransactionReceipt({ hash: tx });
      if (rcpt.status !== "success") {
        setSupplyBanner({ cls: "banner warn", text: txDone("The supply was mined but reverted on chain. Nothing was deposited.", tx) });
        return;
      }
      setSupplyBanner({ cls: "banner ok", text: txDone("Supplied USDS to the lender pool.", tx) });
      lendingRead.refetch();
      tvlRead.refetch();
    } catch (err) {
      setSupplyBanner({ cls: "banner warn", text: humanError(err) });
    } finally {
      setSupplyBusy(false);
    }
  }, [ensureReady, walletClient, publicClient, address, supplyAmt, lendingRead, tvlRead]);

  const runWithdraw = useCallback(async () => {
    if (ensureReady()) return;
    if (!walletClient || !publicClient || !address) return;
    const userShares = L.userShares ?? 0n;
    if (userShares <= 0n) {
      setSupplyBanner({ cls: "banner warn", text: "You have nothing supplied to withdraw." });
      return;
    }
    // withdraw() takes shares. Convert the requested USDS amount to shares
    // and clamp to the full position, so a large amount pulls everything.
    const want = parseDec(supplyAmt, 18);
    let s = userShares;
    if (want > 0n && tvl !== undefined && L.totalShares !== undefined) {
      const est = (want * (L.totalShares + VIRTUAL_SHARES)) / (tvl + VIRTUAL_ASSETS);
      s = est < userShares ? est : userShares;
    }
    if (s <= 0n) {
      setSupplyBanner({ cls: "banner warn", text: "Enter an amount to withdraw." });
      return;
    }
    setSupplyBusy(true);
    try {
      setSupplyBanner({ cls: "banner info", text: "Confirm the withdrawal in your wallet." });
      const { request } = await publicClient.simulateContract({
        address: SHIELDED_LENDING_ADDRESS,
        abi: shieldedLendingAbi,
        functionName: "withdraw",
        args: [s],
        account: address,
      });
      const tx = await walletClient.writeContract(request);
      setSupplyBanner({ cls: "banner info", text: txPending(tx) });
      const rcpt = await publicClient.waitForTransactionReceipt({ hash: tx });
      if (rcpt.status !== "success") {
        setSupplyBanner({ cls: "banner warn", text: txDone("The withdrawal was mined but reverted on chain. Nothing was withdrawn.", tx) });
        return;
      }
      setSupplyBanner({ cls: "banner ok", text: txDone("Withdrew USDS from the lender pool.", tx) });
      lendingRead.refetch();
      tvlRead.refetch();
    } catch (err) {
      setSupplyBanner({ cls: "banner warn", text: humanError(err) });
    } finally {
      setSupplyBusy(false);
    }
  }, [ensureReady, walletClient, publicClient, address, supplyAmt, L.userShares, L.totalShares, tvl, lendingRead, tvlRead]);

  const hasPosition = (L.userShares ?? 0n) > 0n;

  // ---- open loan (real, settles through the shielded pool zk flow) ----
  // openLoan posts 01 collateral out of the shielded pool to the lender (a
  // shielded withdrawal proved in the browser) and sends USDS to the wallet.
  // The loan records the borrower's address as its owner (so only they can
  // repay it) alongside a private ownerCommitment; the collateral source note
  // in the pool stays private.
  const [borrowBusy, setBorrowBusy] = useState(false);
  const [borrowBanner, setBorrowBanner] = useState<{ cls: string; text: ReactNode } | null>(null);

  const runBorrow = useCallback(async () => {
    if (ensureReady()) return;
    if (!walletClient || !publicClient || !address) return;
    const collateral = parseDec(collAmt, 18);
    const borrowAmount = parseDec(borrowAmt, 18);
    if (collateral <= 0n) {
      setBorrowBanner({ cls: "banner warn", text: "Enter how much 01 to post as collateral." });
      return;
    }
    if (borrowAmount <= 0n) {
      setBorrowBanner({ cls: "banner warn", text: "Enter how much USDS to borrow." });
      return;
    }
    setBorrowBusy(true);
    try {
      setBorrowBanner({ cls: "banner info", text: "Sign in your wallet to unlock your private keys." });
      const kp = await ensureKeypair();
      setBorrowBanner({ cls: "banner info", text: "Reading the pool and building a proof in your browser. This can take a minute." });
      const state = await fetchPoolState();
      const prepared = await buildCollateralWithdraw({
        keypair: kp,
        lender: SHIELDED_LENDING_ADDRESS,
        amount: collateral,
        state,
      });
      const [proofArgs, ext] = await toTransactArgs(prepared);
      const ownerCommitment = await ownerCommitmentFor(kp);
      setBorrowBanner({ cls: "banner info", text: "Confirm the loan in your wallet." });
      const { request } = await publicClient.simulateContract({
        address: SHIELDED_LENDING_ADDRESS,
        abi: shieldedLendingAbi,
        functionName: "openLoan",
        args: [proofArgs, ext, borrowAmount, address, address, ownerCommitment],
        account: address,
      });
      const tx = await walletClient.writeContract(request);
      setBorrowBanner({ cls: "banner info", text: txPending(tx) });
      const rcpt = await publicClient.waitForTransactionReceipt({ hash: tx });
      if (rcpt.status !== "success") {
        setBorrowBanner({ cls: "banner warn", text: txDone("The loan was mined but reverted on chain. Nothing was borrowed.", tx) });
        return;
      }
      setBorrowBanner({ cls: "banner ok", text: txDone("Opened a loan. USDS is in your wallet and your 01 is held as collateral.", tx) });
      await refreshPrivateBalance();
      balancesRead.refetch();
      lendingRead.refetch();
      tvlRead.refetch();
      loadLoans();
    } catch (err) {
      setBorrowBanner({ cls: "banner warn", text: humanError(err) });
    } finally {
      setBorrowBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ensureReady, walletClient, publicClient, address, collAmt, borrowAmt, ensureKeypair, refreshPrivateBalance, balancesRead, lendingRead, tvlRead]);

  // ---- repay (real) ----
  // Loans are discovered by the public `to` recipient of each loan (the
  // server scans Borrowed events). For each we read the live debt. A partial
  // repay just transfers USDS in; a full repay also re-shields the collateral
  // back into a private note owned by the borrower (a proved deposit).
  type LoanRow = { id: bigint; collateral: bigint; debt: bigint; health: bigint; open: boolean };
  const [loans, setLoans] = useState<LoanRow[]>([]);
  const [loansBusy, setLoansBusy] = useState(false);
  const [repayId, setRepayId] = useState<bigint | null>(null);
  const [repayBusy, setRepayBusy] = useState(false);
  const [repayBanner, setRepayBanner] = useState<{ cls: string; text: ReactNode } | null>(null);

  const loadLoans = useCallback(async () => {
    if (!publicClient || !address) return;
    setLoansBusy(true);
    try {
      const ids = await fetchLoansFor(address);
      const rows: LoanRow[] = [];
      for (const id of ids) {
        const [info, debt, health] = await Promise.all([
          publicClient.readContract({
            address: SHIELDED_LENDING_ADDRESS,
            abi: shieldedLendingAbi,
            functionName: "loans",
            args: [id],
          }) as Promise<readonly [bigint, bigint, bigint, boolean, `0x${string}`]>,
          publicClient.readContract({
            address: SHIELDED_LENDING_ADDRESS,
            abi: shieldedLendingAbi,
            functionName: "currentDebt",
            args: [id],
          }) as Promise<bigint>,
          publicClient.readContract({
            address: SHIELDED_LENDING_ADDRESS,
            abi: shieldedLendingAbi,
            functionName: "healthFactor",
            args: [id],
          }) as Promise<bigint>,
        ]);
        if (info[3]) rows.push({ id, collateral: info[0], debt, health, open: info[3] });
      }
      setLoans(rows);
      if (rows.length > 0 && (repayId === null || !rows.some((r) => r.id === repayId))) {
        setRepayId(rows[0].id);
      }
      if (rows.length === 0) setRepayId(null);
    } catch {
      // A read failure here just leaves the list as-is; surfaced on action.
    } finally {
      setLoansBusy(false);
    }
  }, [publicClient, address, repayId]);

  useEffect(() => {
    if (view === "borrow" && isConnected && rightNet) loadLoans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, isConnected, rightNet, address]);

  const runRepay = useCallback(async () => {
    if (ensureReady()) return;
    if (!walletClient || !publicClient || !address) return;
    const loan = loans.find((l) => l.id === repayId);
    if (!loan) {
      setRepayBanner({ cls: "banner warn", text: "Pick a loan to repay first." });
      return;
    }
    // The amount is clamped to the live debt by the contract; default to full.
    let amt = repayAmt.trim() === "" ? loan.debt : parseDec(repayAmt, 18);
    if (amt <= 0n) {
      setRepayBanner({ cls: "banner warn", text: "Enter how much USDS to repay." });
      return;
    }
    if (amt > loan.debt) amt = loan.debt;
    const fullRepay = amt >= loan.debt;
    setRepayBusy(true);
    try {
      const usds = (await publicClient.readContract({
        address: SHIELDED_LENDING_ADDRESS,
        abi: shieldedLendingAbi,
        functionName: "usds",
      })) as `0x${string}`;
      const allowance = (await publicClient.readContract({
        address: usds,
        abi: erc20Abi,
        functionName: "allowance",
        args: [address, SHIELDED_LENDING_ADDRESS],
      })) as bigint;
      if (allowance < amt) {
        setRepayBanner({ cls: "banner info", text: "Approve USDS for the lender in your wallet to continue." });
        const { request } = await publicClient.simulateContract({
          address: usds,
          abi: erc20Abi,
          functionName: "approve",
          args: [SHIELDED_LENDING_ADDRESS, amt],
          account: address,
        });
        const aTx = await walletClient.writeContract(request);
        await publicClient.waitForTransactionReceipt({ hash: aTx });
      }
      let reshieldArgs;
      let reshieldExt;
      if (fullRepay) {
        // A full repay returns the collateral to a private note. The lender
        // holds the collateral and self-submits the deposit, so this is a
        // shield to the borrower's own shielded account, proved in browser.
        setRepayBanner({ cls: "banner info", text: "Sign in your wallet to unlock your private keys." });
        const kp = await ensureKeypair();
        setRepayBanner({ cls: "banner info", text: "Building a proof in your browser to re-shield the collateral. This can take a minute." });
        const state = await fetchPoolState();
        const prepared = await buildReshieldDeposit({ keypair: kp, amount: loan.collateral, state });
        [reshieldArgs, reshieldExt] = await toTransactArgs(prepared);
      } else {
        [reshieldArgs, reshieldExt] = emptyTransactArgs();
      }
      setRepayBanner({ cls: "banner info", text: "Confirm the repayment in your wallet." });
      const { request } = await publicClient.simulateContract({
        address: SHIELDED_LENDING_ADDRESS,
        abi: shieldedLendingAbi,
        functionName: "repay",
        args: [loan.id, amt, reshieldArgs, reshieldExt],
        account: address,
      });
      const tx = await walletClient.writeContract(request);
      setRepayBanner({ cls: "banner info", text: txPending(tx) });
      const rcpt = await publicClient.waitForTransactionReceipt({ hash: tx });
      if (rcpt.status !== "success") {
        setRepayBanner({ cls: "banner warn", text: txDone("The repayment was mined but reverted on chain. Nothing was repaid.", tx) });
        return;
      }
      setRepayBanner({
        cls: "banner ok",
        text: txDone(
          fullRepay
            ? "Repaid in full. Your collateral is back in the shielded pool."
            : "Repaid part of the loan.",
          tx,
        ),
      });
      setRepayAmt("");
      if (fullRepay) await refreshPrivateBalance();
      lendingRead.refetch();
      tvlRead.refetch();
      loadLoans();
    } catch (err) {
      setRepayBanner({ cls: "banner warn", text: humanError(err) });
    } finally {
      setRepayBusy(false);
    }
  }, [ensureReady, walletClient, publicClient, address, loans, repayId, repayAmt, ensureKeypair, refreshPrivateBalance, lendingRead, tvlRead, loadLoans]);

  // ---- liquidate (real) ----
  // ShieldedLending lets anyone repay part of an unhealthy loan's debt and seize
  // its 01 collateral at a bonus. Loans are listed from the server's full loan
  // scan; we read each one's live debt, collateral, and health on chain and keep
  // only the liquidatable ones. The seized 01 goes straight to the liquidator's
  // wallet, so no zk proof is involved (this is a public collateral transfer).
  type LiqRow = {
    id: bigint;
    collateral: bigint;
    debt: bigint;
    health: bigint;
    owner: `0x${string}`;
  };
  const [liqLoans, setLiqLoans] = useState<LiqRow[]>([]);
  const [liqBusy, setLiqBusy] = useState(false);
  const [liqId, setLiqId] = useState<bigint | null>(null);
  const [liqAmt, setLiqAmt] = useState("");
  const [liqRunBusy, setLiqRunBusy] = useState(false);
  const [liqBanner, setLiqBanner] = useState<{ cls: string; text: ReactNode } | null>(null);

  const loadLiqLoans = useCallback(async () => {
    if (!publicClient) return;
    setLiqBusy(true);
    try {
      const ids = await fetchAllLoans();
      const rows: LiqRow[] = [];
      for (const id of ids) {
        const [info, debt, liquidatable, health] = await Promise.all([
          publicClient.readContract({
            address: SHIELDED_LENDING_ADDRESS,
            abi: shieldedLendingAbi,
            functionName: "loans",
            args: [id],
          }) as Promise<readonly [bigint, bigint, bigint, boolean, `0x${string}`]>,
          publicClient.readContract({
            address: SHIELDED_LENDING_ADDRESS,
            abi: shieldedLendingAbi,
            functionName: "currentDebt",
            args: [id],
          }) as Promise<bigint>,
          publicClient.readContract({
            address: SHIELDED_LENDING_ADDRESS,
            abi: shieldedLendingAbi,
            functionName: "isLiquidatable",
            args: [id],
          }) as Promise<boolean>,
          publicClient.readContract({
            address: SHIELDED_LENDING_ADDRESS,
            abi: shieldedLendingAbi,
            functionName: "healthFactor",
            args: [id],
          }) as Promise<bigint>,
        ]);
        // Keep open loans the contract itself reports as liquidatable.
        if (info[3] && liquidatable && debt > 0n) {
          rows.push({ id, collateral: info[0], debt, health, owner: info[4] });
        }
      }
      setLiqLoans(rows);
      if (rows.length > 0 && (liqId === null || !rows.some((r) => r.id === liqId))) {
        setLiqId(rows[0].id);
      }
      if (rows.length === 0) setLiqId(null);
    } catch {
      // A read failure leaves the list as-is; surfaced on the action.
    } finally {
      setLiqBusy(false);
    }
  }, [publicClient, liqId]);

  useEffect(() => {
    if (view === "borrow" && rightNet) loadLiqLoans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, rightNet]);

  // The contract caps a single liquidation at CLOSE_FACTOR of the live debt.
  const liqSelected = liqLoans.find((l) => l.id === liqId) ?? null;
  const liqMaxRepay = useMemo(() => {
    if (!liqSelected || L.closeFactor === undefined || L.bps === undefined) return undefined;
    return (liqSelected.debt * L.closeFactor) / L.bps;
  }, [liqSelected, L.closeFactor, L.bps]);

  // Estimate the 01 a liquidator would receive for a given USDS repayment:
  // the repaid value plus the liquidation bonus, converted to 01 at the
  // pool's time-averaged price. This is approximate; the contract is the
  // source of truth and the action is simulated before it is sent.
  const liqSeizeEstimate = useMemo(() => {
    if (
      L.liqBonus === undefined ||
      L.bps === undefined ||
      L.priceAvg === undefined ||
      L.priceAvg === 0n
    ) {
      return undefined;
    }
    const repay = liqAmt.trim() === "" ? liqMaxRepay : parseDec(liqAmt, 18);
    if (repay === undefined || repay <= 0n) return undefined;
    const withBonus = (repay * (L.bps + L.liqBonus)) / L.bps;
    return (withBonus * WAD) / L.priceAvg;
  }, [liqAmt, liqMaxRepay, L.liqBonus, L.bps, L.priceAvg]);

  const runLiquidate = useCallback(async () => {
    if (ensureReady()) return;
    if (!walletClient || !publicClient || !address) return;
    const loan = liqLoans.find((l) => l.id === liqId);
    if (!loan) {
      setLiqBanner({ cls: "banner warn", text: "Pick a loan to liquidate first." });
      return;
    }
    const cap = liqMaxRepay !== undefined ? liqMaxRepay : loan.debt;
    let amt = liqAmt.trim() === "" ? cap : parseDec(liqAmt, 18);
    if (amt <= 0n) {
      setLiqBanner({ cls: "banner warn", text: "Enter how much USDS to repay." });
      return;
    }
    if (amt > cap) amt = cap;
    setLiqRunBusy(true);
    try {
      const usds = (await publicClient.readContract({
        address: SHIELDED_LENDING_ADDRESS,
        abi: shieldedLendingAbi,
        functionName: "usds",
      })) as `0x${string}`;
      const allowance = (await publicClient.readContract({
        address: usds,
        abi: erc20Abi,
        functionName: "allowance",
        args: [address, SHIELDED_LENDING_ADDRESS],
      })) as bigint;
      if (allowance < amt) {
        setLiqBanner({ cls: "banner info", text: "Approve USDS for the lender in your wallet to continue." });
        const { request } = await publicClient.simulateContract({
          address: usds,
          abi: erc20Abi,
          functionName: "approve",
          args: [SHIELDED_LENDING_ADDRESS, amt],
          account: address,
        });
        const aTx = await walletClient.writeContract(request);
        await publicClient.waitForTransactionReceipt({ hash: aTx });
      }
      setLiqBanner({ cls: "banner info", text: "Confirm the liquidation in your wallet." });
      const { request } = await publicClient.simulateContract({
        address: SHIELDED_LENDING_ADDRESS,
        abi: shieldedLendingAbi,
        functionName: "liquidate",
        args: [loan.id, amt, address],
        account: address,
      });
      const tx = await walletClient.writeContract(request);
      setLiqBanner({ cls: "banner info", text: txPending(tx) });
      const rcpt = await publicClient.waitForTransactionReceipt({ hash: tx });
      if (rcpt.status !== "success") {
        setLiqBanner({ cls: "banner warn", text: txDone("The liquidation was mined but reverted on chain. Nothing changed.", tx) });
        return;
      }
      setLiqBanner({
        cls: "banner ok",
        text: txDone("Liquidated. You repaid part of the debt and the seized 01 is in your wallet.", tx),
      });
      setLiqAmt("");
      lendingRead.refetch();
      tvlRead.refetch();
      loadLiqLoans();
    } catch (err) {
      setLiqBanner({ cls: "banner warn", text: humanError(err) });
    } finally {
      setLiqRunBusy(false);
    }
  }, [ensureReady, walletClient, publicClient, address, liqLoans, liqId, liqAmt, liqMaxRepay, lendingRead, tvlRead, loadLiqLoans]);

  function liquidateLabel(): string {
    if (!isConnected) return "Connect wallet";
    if (wrongNet) return "Switch to Ethereum";
    if (liqRunBusy) return "Working…";
    return "Liquidate";
  }

  function supplyActionLabel(base: "supply" | "withdraw"): string {
    if (!isConnected) return "Connect wallet";
    if (wrongNet) return "Switch to Ethereum";
    if (supplyBusy) return "Working…";
    return base === "supply" ? "Supply USDS" : "Withdraw";
  }
  function borrowLabel(): string {
    if (!isConnected) return "Connect wallet";
    if (wrongNet) return "Switch to Ethereum";
    if (borrowBusy) return "Working…";
    return "Open loan";
  }
  function repayLabel(): string {
    if (!isConnected) return "Connect wallet";
    if (wrongNet) return "Switch to Ethereum";
    if (repayBusy) return "Working…";
    return "Repay";
  }
  function usd0ActionLabel(base: "mint" | "redeem"): string {
    if (!isConnected) return "Connect wallet";
    if (wrongNet) return "Switch to Ethereum";
    if (usd0Busy) return "Working…";
    return base === "mint" ? "Mint USD0" : "Redeem USD0";
  }

  const switchClay = wrongNet ? " btn-clay" : "";

  return (
    <>
      <header className="topbar">
        <Link href="/" className="brand">
          <span className="ring" />1
        </Link>
        <nav>
          {(["trade", "private", "usd0"] as View[]).map((v) => (
            <Link key={v} href={`/app/${v}`} className={view === v ? "active" : ""}>
              {v === "trade" ? "Trade" : v === "private" ? "Private" : "USD0"}
            </Link>
          ))}
          <Link href="/app/info" className={view === "info" ? "active" : ""}>
            Info
          </Link>
          <Link href="/app/account" className={view === "account" ? "active" : ""}>
            Account
          </Link>
          <a href="https://zero1.cash/docs">Docs</a>
        </nav>
        <span className="spacer" />
        <ThemeToggle className="theme-toggle" />
        <WalletButton />
      </header>

      {/* persistent live stat strip */}
      <StatStrip />

      <main className="app-main">
        {/* ============ TRADE ============ */}
        <section className={"view" + (view === "trade" ? " active" : "")} id="view-trade">
          <BondStatusCard />
          <div className="panel">
            <div className="panel-pad">
              <div className="section-head">
                <div className="seg" role="group" aria-label="Buy or sell">
                  <button
                    className={!sellMode ? "active" : ""}
                    aria-pressed={!sellMode}
                    onClick={() => {
                      modeTouched.current = true;
                      setSellMode(false);
                    }}
                  >
                    Buy
                  </button>
                  <button
                    className={sellMode ? "active" : ""}
                    aria-pressed={sellMode}
                    onClick={() => {
                      modeTouched.current = true;
                      setSellMode(true);
                    }}
                  >
                    Sell
                  </button>
                </div>
                <span className="sh-sub">
                  {stats.bonded === true ? (
                    <>
                      market{" "}
                      <span className="mono">{market.marketPriceUsd !== null ? usd(market.marketPriceUsd) : "—"}</span>
                      {" · floor "}
                      <span className="mono">{stats.spot !== undefined ? usd(spotNum) : "—"}</span>
                    </>
                  ) : (
                    <>
                      curve spot <span className="mono">{stats.spot !== undefined ? usd(spotNum) : "—"}</span>
                    </>
                  )}
                </span>
              </div>

              {tradeBanner.text ? (
                <div className={tradeBanner.cls}>{tradeBanner.text}</div>
              ) : null}

              {buyClosed ? (
                <div className="buyclosed">
                  <p className="bond-sub">
                    Buying from the curve closed at the bond, permanently. The curve now only buys
                    01 back at the floor, so to buy 01 you use a secondary market like Uniswap, where
                    holders set the price above the floor.
                  </p>
                  {market.marketPriceUsd !== null ? (
                    <p className="note-inline">
                      01 recently traded near{" "}
                      <span className="mono">{usd(market.marketPriceUsd)}</span>
                      {market.marketPoolName ? ` on ${market.marketPoolName}` : ""}. The curve
                      buyback floor is{" "}
                      <span className="mono">{stats.spot !== undefined ? usd(spotNum) : "—"}</span>.
                    </p>
                  ) : null}
                  <a
                    className="btn btn-block btn-lg btn-primary"
                    href={UNISWAP_SWAP_URL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Buy 01 on Uniswap ↗
                  </a>
                  <p className="note-inline" style={{ marginTop: "0.6rem" }}>
                    Already own 01? Use the Sell tab above to sell it to the curve at the floor, or{" "}
                    <Link href="/app/info">see every 01 pool and provide liquidity</Link>.
                  </p>
                </div>
              ) : !sellMode ? (
                <div>
                  <div className="field">
                    <div className="field-head">
                      <label htmlFor="payAmt">You pay</label>
                      <BalTag
                        v={payBalance.v}
                        dec={payBalance.dec}
                        sym={payAsset}
                        onMax={() => {
                          const { v, dec } = payBalance;
                          if (v === undefined) return;
                          let usable = v;
                          if (payAsset === "ETH") {
                            // leave a little ETH for gas so a Max buy can still send
                            const reserve = parseUnits("0.002", 18);
                            usable = v > reserve ? v - reserve : 0n;
                          }
                          setPayAmt(formatUnits(usable, dec));
                        }}
                      />
                    </div>
                    <div className="input-row">
                      <input
                        id="payAmt"
                        inputMode="decimal"
                        placeholder="0.00"
                        value={payAmt}
                        onChange={(e) => setPayAmt(e.target.value)}
                      />
                      <select
                        className="mono asset-select"
                        aria-label="Pay with"
                        value={payAsset}
                        onChange={(e) => setPayAsset(e.target.value as PayAsset)}
                      >
                        <option value="USDS">USDS</option>
                        <option value="USDC">USDC</option>
                        <option value="USDT">USDT</option>
                        <option value="ETH">ETH</option>
                      </select>
                    </div>
                  </div>
                  <div className="field">
                    <div className="field-head">
                      <label htmlFor="recv01">You receive (estimated)</label>
                      <BalTag v={bal01} dec={18} sym="01" />
                    </div>
                    <div className="input-row" style={{ background: "var(--paper)" }}>
                      <input id="recv01" readOnly value={quote?.recv ?? "—"} tabIndex={-1} />
                      <span className="mono" style={{ fontWeight: 600, paddingRight: "0.3rem" }}>
                        01
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="field">
                    <div className="field-head">
                      <label htmlFor="sell01">You sell</label>
                      <BalTag
                        v={bal01}
                        dec={18}
                        sym="01"
                        onMax={() => {
                          if (bal01 === undefined) return;
                          setSell01(formatUnits(bal01, 18));
                        }}
                      />
                    </div>
                    <div className="input-row">
                      <input
                        id="sell01"
                        inputMode="decimal"
                        placeholder="0"
                        value={sell01}
                        onChange={(e) => setSell01(e.target.value)}
                      />
                      <span className="mono" style={{ fontWeight: 600, paddingRight: "0.3rem" }}>
                        01
                      </span>
                    </div>
                  </div>
                  <div className="field">
                    <div className="field-head">
                      <label htmlFor="recvOut">You receive (estimated)</label>
                      <BalTag v={outBalance} dec={outAsset === "USDS" ? 18 : 6} sym={outAsset} />
                    </div>
                    <div className="input-row" style={{ background: "var(--paper)" }}>
                      <input id="recvOut" readOnly value={quote?.recv ?? "—"} tabIndex={-1} />
                      <select
                        className="mono asset-select"
                        aria-label="Receive as"
                        value={outAsset}
                        onChange={(e) => setOutAsset(e.target.value as SellAsset)}
                      >
                        <option value="USDS">USDS</option>
                        <option value="USDC">USDC</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {!buyClosed && (
                <>
              {!sellMode && <BurnSupplyMeter />}

              <div className="quote">
                <div className="quote-row emph">
                  <span className="qr-label">Effective price</span>
                  <span className="qr-val">{quote?.price ?? "—"}</span>
                </div>
                <div className="quote-row">
                  <span className="qr-label">Burn (0.25%, removed from supply)</span>
                  <span className="qr-val clay">{quote?.burn ?? "—"}</span>
                </div>
                <div className="quote-row">
                  <span className="qr-label">Minimum received ({slippagePct || "0"}% slippage)</span>
                  <span className="qr-val">{quote?.min ?? "—"}</span>
                </div>
                <div className="quote-row">
                  <span className="qr-label">Slippage tolerance</span>
                  <span className="qr-val">
                    <input
                      className="slip-input"
                      inputMode="decimal"
                      aria-label="Slippage tolerance percent"
                      value={slippagePct}
                      onChange={(e) => setSlippagePct(e.target.value)}
                    />
                    <span className="mono"> %</span>
                  </span>
                </div>
              </div>

              {quote?.note && (
                <p className="note-inline" style={{ marginTop: "0.4rem" }}>
                  {quote.note}
                </p>
              )}

              {quoteErr && (
                <p className="note-inline" style={{ marginTop: "0.4rem" }}>
                  {quoteErr}
                </p>
              )}

              <button
                className={"btn btn-block btn-lg" + (wrongNet ? " btn-clay" : " btn-primary")}
                onClick={onTradeAction}
                disabled={tradeDisabled}
              >
                {tradeLabel()}
              </button>
                </>
              )}

              <div className="backs">
                <span className="bk-title">What backs this</span>
                <div className="num-meaning">
                  <span className="nm-value mono">
                    {stats.perToken !== undefined ? usd(stats.perToken) : "—"}
                  </span>
                  <span className="nm-label">reserve held per circulating 01</span>
                </div>
                {/* The floor payout follows the curve's own spot quote (what a
                    sell actually pays after the 0.25% burn), not the backing
                    ratio; post-bond the two diverge as burns shrink supply. */}
                <div className="num-meaning">
                  <span className="nm-value mono">
                    {stats.spot !== undefined ? usd(Number(formatUnits(stats.spot, 18)) * 0.9975) : "—"}
                  </span>
                  <span className="nm-label">current buyback floor from the curve, after the burn fee</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ PRIVATE ============ */}
        <section className={"view" + (view === "private" ? " active" : "")} id="view-private">
          <div className="panel private-panel">
            <div className="panel-pad">
              <div className="private-heading">
                <div>
                  <h2>Private pool</h2>
                  <p>Shield, send, and withdraw without exposing transfers between private accounts.</p>
                </div>
                <span className={"private-network" + (rightNet ? " online" : "")}>{rightNet ? "Ethereum mainnet" : "Wallet offline"}</span>
              </div>

              <div className="private-pool-switch" aria-label="Private asset">
                <span>Asset</span>
                <div className="seg">
                  {(["01", "usd0"] as ShieldedPoolId[]).map((p) => (
                    <button
                      key={p}
                      className={privPool === p ? "active" : ""}
                      aria-pressed={privPool === p}
                      onClick={() => onSwitchPrivPool(p)}
                      disabled={privBusy}
                    >
                      {p === "01" ? "01" : "USD0"}
                    </button>
                  ))}
                </div>
              </div>

              <div className={"private-account" + (shieldedAddr ? " ready" : "")}>
                {!isConnected ? (
                  <>
                    <div className="private-account-main">
                      <span className="private-kicker">Your private account</span>
                      <strong>Connect your wallet to begin</strong>
                      <p>Your wallet unlocks the private account and its receive address.</p>
                    </div>
                    <button type="button" className="btn btn-primary" onClick={openConnect}>Connect wallet</button>
                  </>
                ) : wrongNet ? (
                  <>
                    <div className="private-account-main">
                      <span className="private-kicker">Network required</span>
                      <strong>Switch to Ethereum</strong>
                      <p>The private pools settle on Ethereum mainnet.</p>
                    </div>
                    <button type="button" className="btn btn-clay" onClick={() => switchChain({ chainId: mainnet.id })}>Switch network</button>
                  </>
                ) : !shieldedAddr ? (
                  <>
                    <div className="private-account-main">
                      <span className="private-kicker">Connected as {shortAddr(address as string)}</span>
                      <strong>Private balance locked</strong>
                      <p>Sign once to unlock your private balance and receive address.</p>
                    </div>
                    <button type="button" className="btn btn-primary" onClick={onReveal} disabled={privBusy}>
                      {privBusy ? "Unlocking…" : "Unlock private account"}
                    </button>
                  </>
                ) : (
                  <>
                    <div className="private-balance-block">
                      <span className="private-kicker">Private balance</span>
                      <strong className="private-balance mono">
                        {privBalance === undefined ? "Syncing…" : `${fmtUnits(privBalance, 4)} ${privPoolMeta.symbol}`}
                      </strong>
                    </div>
                    <div className="private-address-block">
                      <span className="private-kicker">Receive address</span>
                      <div className="private-address-line">
                        <span className="private-address mono" title={shieldedAddr}>{shieldedAddr}</span>
                        <CopyButton text={shieldedAddr} />
                      </div>
                    </div>
                    <button type="button" className="private-refresh" onClick={onReveal} disabled={privBusy} title="Refresh private balance">
                      {privBusy ? "Syncing…" : "Refresh"}
                    </button>
                  </>
                )}
              </div>

              <p className="private-disclosure">
                Shielding and withdrawing are public Ethereum transactions. Your balance and transfers inside the pool remain private.
              </p>

              {rightNet && shieldedAddr ? (
                <>
                  <div className="subtabs private-action-tabs" aria-label="Private pool action">
                    {(["shield", "send", "unshield"] as PrivTab[]).map((t) => (
                      <button
                        key={t}
                        className={privTab === t ? "active" : ""}
                        onClick={() => setPrivTab(t)}
                      >
                        {t === "shield" ? "Shield" : t === "send" ? "Send privately" : "Withdraw"}
                      </button>
                    ))}
                  </div>

              {!privatePoolConfigured(privPool) && (
                <div className="banner info" style={{ marginBottom: "var(--s-3)" }}>
                  This pool is awaiting deployment configuration. Retired pools are never used as a fallback.
                </div>
              )}

              {privTab !== "shield" && (
                <div className="field private-relayer-field">
                  <label htmlFor="privateRelayer">Relayer</label>
                  <div className="private-relayer-picker">
                    <select
                      id="privateRelayer"
                      value={relayerInfo?.id ?? ""}
                      onChange={(event) => onSelectRelayer(event.target.value)}
                      disabled={privBusy || customRelayerBusy}
                    >
                      {relayerOptions.length === 0 ? <option value="">No relayer available</option> : null}
                      {relayerOptions.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.name}{option.trusted ? " (Trusted)" : ""} - {fmtUnits(BigInt(option.pool.minFee), 4)} {privPoolMeta.symbol}
                        </option>
                      ))}
                      <option value="__custom__">Use custom endpoint…</option>
                    </select>
                    {relayerInfo ? (
                      <div className="private-relayer-meta">
                        <span className={relayerInfo.trusted ? "relayer-badge trusted" : "relayer-badge"}>
                          {relayerInfo.trusted ? "Trusted" : relayerInfo.id === "custom" ? "Custom" : "Community"}
                        </span>
                        <span className="mono">{shortAddr(relayerInfo.relayer)}</span>
                        <span>{relayerFeeText} fee</span>
                        {relayerInfo.id === "custom" ? (
                          <button type="button" className="private-relayer-remove" onClick={onRemoveCustomRelayer} disabled={privBusy || customRelayerBusy}>
                            Remove
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                    {customRelayerOpen ? (
                      <div className="private-custom-relayer">
                        <label htmlFor="customRelayerUrl">Custom relayer URL</label>
                        <div className="private-custom-relayer-row">
                          <input
                            id="customRelayerUrl"
                            type="url"
                            inputMode="url"
                            placeholder="https://relay.example.com"
                            value={customRelayerUrl}
                            onChange={(event) => setCustomRelayerUrl(event.target.value)}
                            disabled={customRelayerBusy}
                            autoComplete="off"
                            spellCheck={false}
                          />
                          <button type="button" onClick={onUseCustomRelayer} disabled={customRelayerBusy || !customRelayerUrl.trim()}>
                            {customRelayerBusy ? "Checking…" : "Verify & use"}
                          </button>
                          <button type="button" className="private-custom-cancel" onClick={() => { setCustomRelayerOpen(false); setCustomRelayerError(null); }} disabled={customRelayerBusy}>
                            Cancel
                          </button>
                        </div>
                        {customRelayerError ? <p className="private-custom-error">{customRelayerError}</p> : null}
                      </div>
                    ) : null}
                  </div>
                  <p className="private-relayer-note">
                    The transfer fee exists because relayers spend gas to submit your transaction for you.
                  </p>
                </div>
              )}

              {privTab === "shield" && (
                <div className="pt-pane">
                  <div className="field">
                    <div className="field-head">
                      <label htmlFor="shieldAmt">{privPoolMeta.symbol} to deposit into the pool</label>
                      <BalTag
                        v={rightNet ? privTokenBal : undefined}
                        dec={18}
                        sym={privPoolMeta.symbol}
                        onMax={() => {
                          if (privTokenBal === undefined) return;
                          setShieldAmt(formatUnits(privTokenBal, 18));
                        }}
                      />
                    </div>
                    <div className="input-row">
                      <input
                        id="shieldAmt"
                        inputMode="decimal"
                        placeholder="0"
                        value={shieldAmt}
                        onChange={(e) => setShieldAmt(e.target.value)}
                      />
                      <span className="mono" style={{ fontWeight: 600, paddingRight: "0.3rem" }}>
                        {privPoolMeta.symbol}
                      </span>
                    </div>
                  </div>
                  <p className="note-inline">
                    After this deposit, this {privPoolMeta.symbol} leaves your public balance. The
                    protocol fee is 0.3% when enabled and is deducted from the private amount you receive.
                    {parseDec(shieldAmt, 18) > 0n
                      ? ` At 30 bps, you receive ${numU(parseDec(shieldAmt, 18) - protocolFee30Bps(parseDec(shieldAmt, 18)), 18, 4)} ${privPoolMeta.symbol} privately.`
                      : ""}
                  </p>
                  <button
                    className={"btn btn-block btn-lg" + (wrongNet ? " btn-clay" : " btn-primary")}
                    style={{ marginTop: "var(--s-3)" }}
                    onClick={onShield}
                    disabled={privBusy || !privatePoolConfigured(privPool)}
                  >
                    {privLabel("shield")}
                  </button>
                </div>
              )}

              {privTab === "send" && (
                <div className="pt-pane">
                  <div className="field">
                    <label htmlFor="sendTo">Recipient shielded address</label>
                    <div className="input-row" style={{ background: "var(--paper)" }}>
                      <input
                        id="sendTo"
                        placeholder="0x..."
                        value={sendTo}
                        onChange={(e) => setSendTo(e.target.value)}
                        style={{ fontSize: "1rem" }}
                      />
                    </div>
                  </div>
                  <div className="field">
                    <div className="field-head">
                      <label htmlFor="sendAmt">Amount</label>
                      {shieldedAddr && privBalance !== undefined ? (
                        <BalTag
                          v={privBalance}
                          dec={18}
                          sym={privPoolMeta.symbol}
                          onMax={() => {
                            const relayerFee = relayerInfo ? BigInt(relayerInfo.pool.minFee) : 0n;
                            const available = privBalance > relayerFee ? privBalance - relayerFee : 0n;
                            setSendAmt(formatUnits(maxPrivateSend(available), 18));
                          }}
                        />
                      ) : null}
                    </div>
                    <div className="input-row">
                      <input
                        id="sendAmt"
                        inputMode="decimal"
                        placeholder="0"
                        value={sendAmt}
                        onChange={(e) => setSendAmt(e.target.value)}
                      />
                      <span className="mono" style={{ fontWeight: 600, paddingRight: "0.3rem" }}>
                        {privPoolMeta.symbol}
                      </span>
                    </div>
                  </div>
                  <p className="note-inline">
                    A relayer submits the transfer, so the sender wallet does not appear on chain.
                    The 0.3% protocol fee and relayer fee are paid from private notes.
                  </p>
                  <button
                    className={"btn btn-block btn-lg" + (wrongNet ? " btn-clay" : " btn-primary")}
                    style={{ marginTop: "var(--s-3)" }}
                    onClick={onSend}
                    disabled={privBusy || !privatePoolConfigured(privPool)}
                  >
                    {privLabel("send")}
                  </button>
                </div>
              )}

              {privTab === "unshield" && (
                <div className="pt-pane">
                  <div className="field">
                    <div className="field-head">
                      <label htmlFor="unAmt">Amount to withdraw</label>
                      {shieldedAddr && privBalance !== undefined ? (
                        <BalTag
                          v={privBalance}
                          dec={18}
                          sym={privPoolMeta.symbol}
                          onMax={() => {
                            const relayerFee = relayerInfo ? BigInt(relayerInfo.pool.minFee) : 0n;
                            setUnAmt(formatUnits(privBalance > relayerFee ? privBalance - relayerFee : 0n, 18));
                          }}
                        />
                      ) : null}
                    </div>
                    <div className="input-row">
                      <input
                        id="unAmt"
                        inputMode="decimal"
                        placeholder="0"
                        value={unAmt}
                        onChange={(e) => setUnAmt(e.target.value)}
                      />
                      <span className="mono" style={{ fontWeight: 600, paddingRight: "0.3rem" }}>
                        {privPoolMeta.symbol}
                      </span>
                    </div>
                  </div>
                  <div className="field">
                    <label htmlFor="unTo">Destination public address</label>
                    <div className="input-row" style={{ background: "var(--paper)" }}>
                      <input
                        id="unTo"
                        placeholder="0x..."
                        value={unTo}
                        onChange={(e) => setUnTo(e.target.value)}
                        style={{ fontSize: "1rem" }}
                      />
                    </div>
                  </div>
                  <p className="note-inline">
                    Withdrawing reveals the amount and destination, but not the originating wallet.
                    The recipient receives the entered amount minus the 0.3% protocol fee. The separate
                    relayer fee of {relayerFeeText} also comes from private notes.
                    {parseDec(unAmt, 18) > 0n
                      ? ` At 30 bps, the recipient receives ${numU(withdrawalGross - protocolFee30Bps(withdrawalGross), 18, 4)} ${privPoolMeta.symbol}, and your total private spend is ${numU(withdrawalPrivateSpend, 18, 4)} ${privPoolMeta.symbol}.`
                      : ""}
                  </p>
                  <button
                    className={"btn btn-block btn-lg" + (wrongNet ? " btn-clay" : " btn-primary")}
                    style={{ marginTop: "var(--s-3)" }}
                    onClick={onUnshield}
                    disabled={privBusy || !privatePoolConfigured(privPool)}
                  >
                    {privLabel("unshield")}
                  </button>
                </div>
              )}
                </>
              ) : null}

              {privBanner && (
                <div className={privBanner.cls} style={{ marginTop: "var(--s-3)", marginBottom: 0 }}>
                  {privBanner.text}
                </div>
              )}

              <ProvingProgress />

              <div className="private-footnote">
                <span>Proofs run in this browser</span>
                <span>Your viewing key never reaches the relayer</span>
              </div>
            </div>
          </div>
        </section>

        {/* ============ BORROW ============ */}
        <section className={"view" + (view === "borrow" ? " active" : "")} id="view-borrow">
          <div className="cols">
            <div className="panel">
              <div className="panel-pad">
                <h2 style={{ fontSize: "1.2rem", marginBottom: "0.3rem" }}>Supply USDS</h2>
                <p className="note-inline" style={{ marginTop: 0 }}>
                  The lender pool funds the loans and earns interest, plus the sUSDS rate on idle
                  funds.
                </p>
                <div className="quote" style={{ marginTop: "var(--s-3)" }}>
                  <div className="quote-row">
                    <span className="qr-label">Borrow rate paid into the pool</span>
                    <span className="qr-val accent">{rateStr}</span>
                  </div>
                  <div className="quote-row">
                    <span className="qr-label">Your position</span>
                    <span className="qr-val">{positionStr}</span>
                  </div>
                </div>
                <div className="field" style={{ marginTop: "var(--s-2)" }}>
                  <label htmlFor="supplyAmt">Amount (USDS)</label>
                  <div className="input-row">
                    <input
                      id="supplyAmt"
                      inputMode="decimal"
                      placeholder="0"
                      value={supplyAmt}
                      onChange={(e) => setSupplyAmt(e.target.value)}
                    />
                    <span className="mono" style={{ fontWeight: 600, paddingRight: "0.3rem" }}>
                      USDS
                    </span>
                  </div>
                </div>
                <button
                  className={"btn btn-block" + (wrongNet ? " btn-clay" : " btn-primary")}
                  onClick={runSupply}
                  disabled={supplyBusy}
                >
                  {supplyActionLabel("supply")}
                </button>
                <button
                  className={"btn btn-block" + switchClay}
                  style={{ marginTop: "var(--s-2)" }}
                  onClick={runWithdraw}
                  disabled={supplyBusy || !hasPosition}
                >
                  {supplyActionLabel("withdraw")}
                </button>
                {supplyBanner && (
                  <div className={supplyBanner.cls} style={{ marginTop: "var(--s-3)", marginBottom: 0 }}>
                    {supplyBanner.text}
                  </div>
                )}
              </div>
            </div>

            <div className="panel">
              <div className="panel-pad">
                <h2 style={{ fontSize: "1.2rem", marginBottom: "0.3rem" }}>Borrow USDS</h2>
                <p className="note-inline" style={{ marginTop: 0 }}>
                  Borrow against 01, valued by the curve itself at a conservative, time-averaged
                  price.
                </p>
                <div className="field" style={{ marginTop: "var(--s-3)" }}>
                  <label htmlFor="collAmt">Collateral (01)</label>
                  <div className="input-row">
                    <input
                      id="collAmt"
                      inputMode="decimal"
                      placeholder="0"
                      value={collAmt}
                      onChange={(e) => setCollAmt(e.target.value)}
                    />
                    <span className="mono" style={{ fontWeight: 600, paddingRight: "0.3rem" }}>
                      01
                    </span>
                  </div>
                </div>
                <div className="quote">
                  <div className="quote-row">
                    <span className="qr-label">Conservative value (haircut, not spot)</span>
                    <span className="qr-val">{borrow.collVal}</span>
                  </div>
                  <div className="quote-row emph">
                    <span className="qr-label">Max borrow at 35% LTV</span>
                    <span className="qr-val">{borrow.maxBorrow}</span>
                  </div>
                  <div className="quote-row">
                    <span className="qr-label">Interest rate</span>
                    <span className="qr-val">{borrow.interest}</span>
                  </div>
                </div>
                <div className="field">
                  <label htmlFor="borrowAmt">Borrow USDS</label>
                  <div className="input-row">
                    <input
                      id="borrowAmt"
                      inputMode="decimal"
                      placeholder="0"
                      value={borrowAmt}
                      onChange={(e) => setBorrowAmt(e.target.value)}
                    />
                    <span className="mono" style={{ fontWeight: 600, paddingRight: "0.3rem" }}>
                      USDS
                    </span>
                  </div>
                </div>
                <div
                  style={{
                    fontSize: "0.82rem",
                    color: "var(--ink-2)",
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "0.3rem",
                  }}
                >
                  <span>Loan health</span>
                  <span className="mono">{borrow.healthNum}</span>
                </div>
                <div className={borrow.cls}>
                  <span style={{ width: `${borrow.pct}%` }} />
                </div>
                <button
                  className={"btn btn-block" + (wrongNet ? " btn-clay" : " btn-primary")}
                  style={{ marginTop: "var(--s-3)" }}
                  onClick={runBorrow}
                  disabled={borrowBusy}
                >
                  {borrowLabel()}
                </button>
                {borrowBanner && (
                  <div className={borrowBanner.cls} style={{ marginTop: "var(--s-3)", marginBottom: 0 }}>
                    {borrowBanner.text}
                  </div>
                )}
              </div>
            </div>

            <div className="panel">
              <div className="panel-pad">
                <h2 style={{ fontSize: "1.2rem", marginBottom: "0.3rem" }}>Repay a loan</h2>
                <p className="note-inline" style={{ marginTop: 0 }}>
                  This lists the loans whose USDS was paid out to the connected wallet, so you can
                  repay them. The 01 collateral comes from your shielded pool notes and, on a full
                  repay, returns there as a private note.
                </p>
                {!isConnected || wrongNet ? (
                  <div className="banner info" style={{ marginTop: "var(--s-3)", marginBottom: 0 }}>
                    Connect your wallet on Ethereum to see your loans.
                  </div>
                ) : loansBusy && loans.length === 0 ? (
                  <div className="banner info" style={{ marginTop: "var(--s-3)", marginBottom: 0 }}>
                    Reading your loans.
                  </div>
                ) : loans.length === 0 ? (
                  <div className="banner info" style={{ marginTop: "var(--s-3)", marginBottom: 0 }}>
                    No open loans were found for this wallet.
                  </div>
                ) : (
                  <>
                    <div className="field" style={{ marginTop: "var(--s-3)" }}>
                      <label htmlFor="repayLoan">Loan</label>
                      <div className="input-row">
                        <select
                          id="repayLoan"
                          value={repayId !== null ? repayId.toString() : ""}
                          onChange={(e) => setRepayId(BigInt(e.target.value))}
                          style={{ flex: 1, background: "transparent", border: "none", color: "inherit" }}
                        >
                          {loans.map((l) => (
                            <option key={l.id.toString()} value={l.id.toString()}>
                              Loan #{l.id.toString()} — {numU(l.debt, 18, 2)} USDS owed
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {(() => {
                      const loan = loans.find((l) => l.id === repayId);
                      if (!loan) return null;
                      return (
                        <div className="quote">
                          <div className="quote-row">
                            <span className="qr-label">Collateral held</span>
                            <span className="qr-val">{numU(loan.collateral, 18, 2)} 01</span>
                          </div>
                          <div className="quote-row emph">
                            <span className="qr-label">Debt now</span>
                            <span className="qr-val">{numU(loan.debt, 18, 2)} USDS</span>
                          </div>
                        </div>
                      );
                    })()}
                    <div className="field">
                      <label htmlFor="repayAmt">Repay USDS (blank repays in full)</label>
                      <div className="input-row">
                        <input
                          id="repayAmt"
                          inputMode="decimal"
                          placeholder="Full debt"
                          value={repayAmt}
                          onChange={(e) => setRepayAmt(e.target.value)}
                        />
                        <span className="mono" style={{ fontWeight: 600, paddingRight: "0.3rem" }}>
                          USDS
                        </span>
                      </div>
                    </div>
                    <button
                      className={"btn btn-block" + (wrongNet ? " btn-clay" : " btn-primary")}
                      style={{ marginTop: "var(--s-3)" }}
                      onClick={runRepay}
                      disabled={repayBusy}
                    >
                      {repayLabel()}
                    </button>
                  </>
                )}
                {repayBanner && (
                  <div className={repayBanner.cls} style={{ marginTop: "var(--s-3)", marginBottom: 0 }}>
                    {repayBanner.text}
                  </div>
                )}
              </div>
            </div>

            <div className="panel">
              <div className="panel-pad">
                <h2 style={{ fontSize: "1.2rem", marginBottom: "0.3rem" }}>Liquidate a loan</h2>
                <p className="note-inline" style={{ marginTop: 0 }}>
                  Loans below the liquidation threshold can be partly repaid by anyone, who then
                  seizes 01 collateral at a bonus. This lists the loans the contract reports as
                  liquidatable now. You pay USDS and the seized 01 comes to your wallet.
                </p>
                {!isConnected || wrongNet ? (
                  <div className="banner info" style={{ marginTop: "var(--s-3)", marginBottom: 0 }}>
                    Connect your wallet on Ethereum to liquidate a loan.
                  </div>
                ) : liqBusy && liqLoans.length === 0 ? (
                  <div className="banner info" style={{ marginTop: "var(--s-3)", marginBottom: 0 }}>
                    Reading loans for liquidation.
                  </div>
                ) : liqLoans.length === 0 ? (
                  <div className="banner info" style={{ marginTop: "var(--s-3)", marginBottom: 0 }}>
                    No loans are below the liquidation threshold right now.
                  </div>
                ) : (
                  <>
                    <div className="field" style={{ marginTop: "var(--s-3)" }}>
                      <label htmlFor="liqLoan">Loan</label>
                      <div className="input-row">
                        <select
                          id="liqLoan"
                          value={liqId !== null ? liqId.toString() : ""}
                          onChange={(e) => setLiqId(BigInt(e.target.value))}
                          style={{ flex: 1, background: "transparent", border: "none", color: "inherit" }}
                        >
                          {liqLoans.map((l) => (
                            <option key={l.id.toString()} value={l.id.toString()}>
                              Loan #{l.id.toString()} — {numU(l.debt, 18, 2)} USDS owed
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {liqSelected && (
                      <div className="quote">
                        <div className="quote-row">
                          <span className="qr-label">Collateral held</span>
                          <span className="qr-val">{numU(liqSelected.collateral, 18, 2)} 01</span>
                        </div>
                        <div className="quote-row">
                          <span className="qr-label">Debt now</span>
                          <span className="qr-val">{numU(liqSelected.debt, 18, 2)} USDS</span>
                        </div>
                        <div className="quote-row">
                          <span className="qr-label">Health factor</span>
                          <span className="qr-val">{Number(formatUnits(liqSelected.health, 18)).toFixed(2)}</span>
                        </div>
                        {liqMaxRepay !== undefined && (
                          <div className="quote-row">
                            <span className="qr-label">Most you can repay now</span>
                            <span className="qr-val">{numU(liqMaxRepay, 18, 2)} USDS</span>
                          </div>
                        )}
                        {liqSeizeEstimate !== undefined && (
                          <div className="quote-row emph">
                            <span className="qr-label">Estimated 01 seized (approximate)</span>
                            <span className="qr-val">{numU(liqSeizeEstimate, 18, 2)} 01</span>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="field">
                      <label htmlFor="liqAmt">Repay USDS (blank repays the most allowed)</label>
                      <div className="input-row">
                        <input
                          id="liqAmt"
                          inputMode="decimal"
                          placeholder="Max allowed"
                          value={liqAmt}
                          onChange={(e) => setLiqAmt(e.target.value)}
                        />
                        <span className="mono" style={{ fontWeight: 600, paddingRight: "0.3rem" }}>
                          USDS
                        </span>
                      </div>
                    </div>
                    <button
                      className={"btn btn-block" + (wrongNet ? " btn-clay" : " btn-primary")}
                      style={{ marginTop: "var(--s-3)" }}
                      onClick={runLiquidate}
                      disabled={liqRunBusy}
                    >
                      {liquidateLabel()}
                    </button>
                  </>
                )}
                {liqBanner && (
                  <div className={liqBanner.cls} style={{ marginTop: "var(--s-3)", marginBottom: 0 }}>
                    {liqBanner.text}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ============ USD0 ============ */}
        <section className={"view usd0-view" + (view === "usd0" ? " active" : "")} id="view-usd0">
          <div className="usd0-heading">
            <div>
              <h2>USD0</h2>
              <p>A value-accruing dollar backed by USDS held in sUSDS.</p>
            </div>
            <Link className="usd0-private-link" href="/app/private">
              Shield USD0
            </Link>
          </div>
          {(!PRIVACY_CONFIG.usd0 || !PRIVACY_CONFIG.usd0Gateway) && (
            <div className="banner info" style={{ marginBottom: "var(--s-3)" }}>
              USD0 is awaiting deployment configuration. No legacy vault will be used.
            </div>
          )}
          <div className="usd0-kpis">
            <div className="usd0-kpi">
              <span className="v mono accent">{usd0TvlStr}</span>
              <span className="k">Reserve value</span>
            </div>
            <div className="usd0-kpi">
              <span className="v mono">{usd0SupplyStr}</span>
              <span className="k">Circulating supply</span>
            </div>
            <div className="usd0-kpi">
              <span className="v mono">{usd0BackingStr}</span>
              <span className="k">Value per USD0</span>
            </div>
            <div className="usd0-kpi">
              <span className="v mono">{usd0HolderReserveStr}</span>
              <span className="k">Holder reserve</span>
            </div>
          </div>
          <div className="usd0-trade">
            <div className="usd0-trade-head">
              <div>
                <span className="usd0-eyebrow">Convert</span>
                <h3>{usd0Mode === "mint" ? "USDC to USD0" : "USD0 to USDC"}</h3>
              </div>
              <div className="seg" role="tablist" aria-label="USD0 action">
                <button
                  type="button"
                  role="tab"
                  aria-selected={usd0Mode === "mint"}
                  aria-pressed={usd0Mode === "mint"}
                  onClick={() => setUsd0Mode("mint")}
                >
                  Mint
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={usd0Mode === "redeem"}
                  aria-pressed={usd0Mode === "redeem"}
                  onClick={() => setUsd0Mode("redeem")}
                >
                  Redeem
                </button>
              </div>
            </div>

            <div className="usd0-trade-body">
              {usd0Mode === "mint" ? (
                <>
                  <p className="usd0-action-note">Deposit USDC at the current reserve exchange rate.</p>
                  <div className="field">
                    <div className="field-head">
                      <label htmlFor="usd0MintAmt">You deposit</label>
                      <span className="bal-tag">
                        Balance <span className="mono">{rightNet && balUsdc !== undefined ? numU(balUsdc, 6, 2) : "—"} USDC</span>
                        {rightNet && balUsdc !== undefined && balUsdc > 0n && (
                          <button className="bal-max" type="button" onClick={() => setUsd0MintAmt(formatUnits(balUsdc, 6))}>
                            Max
                          </button>
                        )}
                      </span>
                    </div>
                    <div className="input-row">
                      <input
                        id="usd0MintAmt"
                        inputMode="decimal"
                        placeholder="0"
                        value={usd0MintAmt}
                        onChange={(e) => setUsd0MintAmt(e.target.value)}
                      />
                      <span className="usd0-input-asset mono">USDC</span>
                    </div>
                  </div>
                  <div className="quote usd0-quote">
                    <div className="quote-row emph">
                      <span className="qr-label">You receive</span>
                      <span className="qr-val">
                        {usd0MintOut !== undefined ? num(usd0MintOut, 2) + " USD0" : "—"}
                      </span>
                    </div>
                    <div className="quote-row">
                      <span className="qr-label">USD0 wallet balance</span>
                      <span className="qr-val">{usd0WalletStr}</span>
                    </div>
                  </div>
                  <button
                    className={"btn btn-block" + (wrongNet ? " btn-clay" : " btn-primary")}
                    onClick={runUsd0Mint}
                    disabled={usd0Busy || !PRIVACY_CONFIG.usd0 || !PRIVACY_CONFIG.usd0Gateway}
                  >
                    {usd0ActionLabel("mint")}
                  </button>
                </>
              ) : (
                <>
                  <p className="usd0-action-note">Redeem USD0 for USDC through the reserve gateway.</p>
                  <div className="field">
                    <div className="field-head">
                      <label htmlFor="usd0RedeemAmt">You redeem</label>
                      <span className="bal-tag">
                        Balance <span className="mono">{usd0WalletStr}</span>
                        {U.bal !== undefined && U.bal > 0n && (
                          <button className="bal-max" type="button" onClick={() => setUsd0RedeemAmt(formatUnits(U.bal!, 18))}>
                            Max
                          </button>
                        )}
                      </span>
                    </div>
                    <div className="input-row">
                      <input
                        id="usd0RedeemAmt"
                        inputMode="decimal"
                        placeholder="0"
                        value={usd0RedeemAmt}
                        onChange={(e) => setUsd0RedeemAmt(e.target.value)}
                      />
                      <span className="usd0-input-asset mono">USD0</span>
                    </div>
                  </div>
                  <div className="quote usd0-quote">
                    <div className="quote-row emph">
                      <span className="qr-label">You receive</span>
                      <span className="qr-val">
                        {usd0RedeemOut !== undefined ? num(usd0RedeemOut, 2) + " USDC" : "—"}
                      </span>
                    </div>
                    <div className="quote-row">
                      <span className="qr-label">Minimum received</span>
                      <span className="qr-val">
                        {usd0RedeemMin !== undefined ? num(usd0RedeemMin, 2) + " USDC" : "—"}
                      </span>
                    </div>
                  </div>
                  <button
                    className={"btn btn-block" + (wrongNet ? " btn-clay" : " btn-primary")}
                    onClick={runUsd0Redeem}
                    disabled={usd0Busy || !PRIVACY_CONFIG.usd0 || !PRIVACY_CONFIG.usd0Gateway}
                  >
                    {usd0ActionLabel("redeem")}
                  </button>
                </>
              )}
              {usd0Banner && <div className={usd0Banner.cls}>{usd0Banner.text}</div>}
            </div>
            <div className="usd0-trade-foot">
              <span>80% of reserve growth increases USD0 value.</span>
            </div>
          </div>
          <div className="info-panel usd0-explainer">
            <h3 className="info-h">How USD0 works</h3>
            <p className="note-inline">
              USDC enters through the Sky conversion gateway and is held as yield-bearing sUSDS. USD0
              represents the holder-owned reserve, so its redemption value can increase as that reserve
              grows. Minting and redeeming are public Ethereum transactions. To hold or send USD0
              privately, move it into the USD0 pool from the <Link href="/app/private">Private tab</Link>.
            </p>
          </div>
        </section>

        {/* ============ ACCOUNT ============ */}
        <section className={"view" + (view === "account" ? " active" : "")} id="view-account">
          <div className="section-head">
            <h2>Account</h2>
            <span className="sh-sub">{acctAddr}</span>
          </div>
          {/* headline figures */}
          <div className="info-kpis">
            <div className="info-kpi">
              <span className="v mono accent">
                {rightNet && acct.pub01 !== undefined ? num(acct.pub01, 2) + " 01" : "—"}
              </span>
              <span className="k">public 01 in this wallet, visible on chain</span>
            </div>
            <div className="info-kpi">
              <span className="v mono">
                {rightNet && acct.value01 !== undefined ? usd2(acct.value01) : "—"}
              </span>
              <span className="k">value of your public 01 at the curve price</span>
            </div>
            <div className="info-kpi">
              <span className="v mono">
                {rightNet && acct.stables !== undefined ? usd2(acct.stables) : "—"}
              </span>
              <span className="k">USDS, USDC and USDT combined</span>
            </div>
          </div>

          {/* ---- your 01 ---- */}
          <div className="info-panel">
            <h3 className="info-h">Your 01</h3>
            <div className="acct-grid">
              <div className="acct-cell">
                <div className="num-meaning">
                  <span className="nm-value mono">
                    {rightNet ? (bal01 !== undefined ? fmtUnits(bal01, 2) + " 01" : "loading…") : "—"}
                  </span>
                  <span className="nm-label">public 01 balance, visible on chain</span>
                </div>
              </div>
              <div className="acct-cell acct-wide">
                <div className="num-meaning">
                  <span className="nm-value mono">
                    {rightNet && acct.value01 !== undefined ? usd(acct.value01, 2) : "—"}
                  </span>
                  <span className="nm-label">value of your public 01 at the curve price</span>
                </div>
              </div>
            </div>
          </div>

          {/* ---- wallet balances ---- */}
          <div className="info-panel">
            <h3 className="info-h">Wallet balances</h3>
            <div className="acct-grid">
              <div className="acct-cell">
                <div className="num-meaning">
                  <span className="nm-value mono">
                    {rightNet ? (balEth !== undefined ? numU(balEth, 18, 4) + " ETH" : "loading…") : "—"}
                  </span>
                  <span className="nm-label">ETH balance, for gas and buys</span>
                </div>
              </div>
              <div className="acct-cell">
                <div className="num-meaning">
                  <span className="nm-value mono">
                    {rightNet ? (balUsds !== undefined ? fmtUnits(balUsds, 2) + " USDS" : "loading…") : "—"}
                  </span>
                  <span className="nm-label">USDS balance in your wallet</span>
                </div>
              </div>
              <div className="acct-cell">
                <div className="num-meaning">
                  <span className="nm-value mono">
                    {rightNet ? (balUsdc !== undefined ? numU(balUsdc, 6, 2) + " USDC" : "loading…") : "—"}
                  </span>
                  <span className="nm-label">USDC balance in your wallet</span>
                </div>
              </div>
              <div className="acct-cell">
                <div className="num-meaning">
                  <span className="nm-value mono">
                    {rightNet ? (balUsdt !== undefined ? numU(balUsdt, 6, 2) + " USDT" : "loading…") : "—"}
                  </span>
                  <span className="nm-label">USDT balance in your wallet</span>
                </div>
              </div>
            </div>
          </div>

          {/* ---- legacy lender position, intentionally unavailable in ---- */}
          <div className="info-panel" style={{ display: "none" }}>
            <h3 className="info-h">Lender position</h3>
            <div className="imeter">
              <div className="imeter-head">
                <span className="imeter-label">Your share of the lender pool</span>
                <span className="imeter-val mono">
                  {rightNet ? pctStr(acct.poolShare, 2) : "—"}
                </span>
              </div>
              <div className="imeter-track">
                {rightNet && acct.poolShare != null && Number.isFinite(acct.poolShare) && (
                  <div
                    className="imeter-fill"
                    style={{
                      width:
                        (acct.poolShare > 0 ? Math.max(Math.min(acct.poolShare, 100), 0.75) : 0) + "%",
                    }}
                  />
                )}
              </div>
              <div className="imeter-sub">
                {rightNet && acct.supplied !== undefined
                  ? `${num(acct.supplied, 2)} USDS supplied, earning the pool's supply rate`
                  : "supply USDS in Borrow to open a position"}
              </div>
            </div>
            <div className="acct-grid">
              <div className="acct-cell acct-wide">
                <div className="num-meaning">
                  <span className="nm-value mono">{positionStr}</span>
                  <span className="nm-label">USDS supplied to the lender pool</span>
                </div>
              </div>
            </div>
          </div>

          {/* ---- your loans ---- */}
          <div className="info-panel" style={{ display: "none" }}>
            <h3 className="info-h">Your loans</h3>
            <p className="note-inline" style={{ marginTop: 0 }}>
              The loans whose USDS was paid out to this wallet. Health is the liquidation ceiling over
              what you owe now; a lower figure means less room before liquidation. Repay in the Borrow
              tab.
            </p>
            {!isConnected || wrongNet ? (
              <div className="banner info" style={{ marginTop: "var(--s-3)", marginBottom: 0 }}>
                Connect your wallet on Ethereum to see your loans.
              </div>
            ) : loansBusy && loans.length === 0 ? (
              <div className="banner info" style={{ marginTop: "var(--s-3)", marginBottom: 0 }}>
                Reading your loans.
              </div>
            ) : loans.length === 0 ? (
              <div className="banner info" style={{ marginTop: "var(--s-3)", marginBottom: 0 }}>
                No open loans were found for this wallet.
              </div>
            ) : (
              <div style={{ display: "grid", gap: "var(--s-3)" }}>
                {loans.map((l) => {
                  const h = Number(formatUnits(l.health, 18));
                  const healthNum = !Number.isFinite(h) || h > 9 ? "∞" : h.toFixed(2);
                  const pct = Number.isFinite(h) ? Math.max(4, Math.min(100, (h / 2.5) * 100)) : 100;
                  const cls = "health" + (Number.isFinite(h) && h < 1.15 ? " low" : Number.isFinite(h) && h < 1.6 ? " mid" : "");
                  return (
                    <div key={l.id.toString()} className="quote">
                      <div className="quote-row">
                        <span className="qr-label">Loan #{l.id.toString()} collateral held</span>
                        <span className="qr-val">{numU(l.collateral, 18, 2)} 01</span>
                      </div>
                      <div className="quote-row emph">
                        <span className="qr-label">Debt now</span>
                        <span className="qr-val">{numU(l.debt, 18, 2)} USDS</span>
                      </div>
                      <div
                        style={{
                          fontSize: "0.82rem",
                          color: "var(--ink-2)",
                          display: "flex",
                          justifyContent: "space-between",
                          margin: "0.3rem 0",
                        }}
                      >
                        <span>Loan health</span>
                        <span className="mono">{healthNum}</span>
                      </div>
                      <div className={cls}>
                        <span style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ---- shielded identity ---- */}
          <div className="info-panel">
            <h3 className="info-h">Shielded identity</h3>
            <div className="acct-grid">
              <div className="acct-cell acct-wide">
                <div className="num-meaning">
                  <span className="nm-value mono" style={{ wordBreak: "break-all" }}>
                    {shieldedAddr ? (
                      <>
                        {shieldedAddr}
                        <CopyButton text={shieldedAddr} />
                      </>
                    ) : rightNet ? (
                      <button
                        type="button"
                        onClick={onReveal}
                        disabled={privBusy}
                        style={{
                          background: "none",
                          border: 0,
                          padding: 0,
                          cursor: "pointer",
                          color: "var(--accent)",
                          textDecoration: "underline",
                          font: "inherit",
                        }}
                      >
                        {privBusy ? "working…" : "reveal"}
                      </button>
                    ) : (
                      "—"
                    )}
                  </span>
                  <span className="nm-label">
                    your shielded address, share it to receive private transfers
                  </span>
                </div>
              </div>
            </div>
            <p className="note-inline">
              Balances read straight from the chain. There is no transaction list here. To see your
              full history, open your address on{" "}
              {rightNet && address ? (
                <a href={explorerAddressUrl(address)} target="_blank" rel="noreferrer">
                  Etherscan
                </a>
              ) : (
                "Etherscan"
              )}
              . Private transfers do not appear there, since the browser is the only place they are
              visible.
            </p>
          </div>
        </section>

        {/* ============ INFO ============ */}
        {view === "info" && <InfoPanel />}
      </main>

      <nav className="mobnav" aria-label="Sections">
        {(["trade", "private", "usd0"] as View[]).map((v) => (
          <Link key={v} href={`/app/${v}`} className={view === v ? "active" : ""}>
            {v === "trade" ? "Trade" : v === "private" ? "Private" : "USD0"}
          </Link>
        ))}
        <Link href="/app/info" className={view === "info" ? "active" : ""}>
          Info
        </Link>
        <Link href="/app/account" className={view === "account" ? "active" : ""}>
          Account
        </Link>
        <a href="https://zero1.cash/docs">Docs</a>
      </nav>
    </>
  );
}
